import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/rn3";

// Translation dictionaries. Keep keys flat and namespaced (e.g. "nav.home").
// Missing keys fall back to the English value (or the key itself if not found).
export const TRANSLATIONS = {
  en: {
    // Top navigation
    "nav.home": "Home",
    "nav.about": "About Us",
    "nav.services": "Services",
    "nav.appointments": "Appointments",
    "nav.intake": "Client Intake",
    "nav.portal": "Admin Portal",
    "nav.resources": "Resources",
    "nav.contact": "Contact",
    "nav.admin": "Admin",
    "nav.sign_out": "Sign Out",
    "nav.sign_in": "Sign In",
    // Common buttons / labels
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.submit": "Submit",
    "common.loading": "Loading…",
    "common.search": "Search",
    "common.back": "Back",
    "common.continue": "Continue",
    "common.confirm": "Confirm",
    "common.yes": "Yes",
    "common.no": "No",
    "common.email": "Email",
    "common.phone": "Phone",
    "common.name": "Name",
    "common.full_name": "Full Name",
    "common.message": "Message",
    "common.required": "Required",
    "common.optional": "Optional",
    "common.language": "Language",
    "common.english": "English",
    "common.spanish": "Spanish",
    // Footer
    "footer.tagline": "Solutions · Strategy · Structure",
    "footer.copyright": "All rights reserved.",
    "footer.powered_by": "Powered by",
    "footer.contact": "Contact",
    "footer.connect": "Connect",
    "footer.services": "Services",
    "footer.legal": "Legal",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    // Services
    "service.bookkeeping": "Bookkeeping",
    "service.tax_prep": "Tax Preparation",
    "service.sales_tax": "Sales Tax",
    "service.notary": "Notary",
    "service.loan_signing": "Loan Signing",
    "service.itin": "ITIN",
    "service.translation": "Translation",
    // Public CTAs / messages
    "cta.book_consult": "Book a Free Consultation",
    "cta.get_started": "Get Started",
    "cta.refer_friend": "Refer a Friend",
    "cta.upload_docs": "Upload Documents",
    "cta.view_reports": "View Reports",
    "cta.start_intake": "Start an Intake",
    // Auth
    "auth.welcome_back": "Welcome back",
    "auth.password": "Password",
    "auth.password_hint": "At least 8 characters.",
    // Forgot password page
    "forgot.eyebrow": "Account Recovery",
    "forgot.title": "Reset your password",
    "forgot.intro": "Enter the email tied to your portal account. We'll send a link to set a new password.",
    "forgot.email_label": "Email",
    "forgot.submit": "Send Reset Link",
    "forgot.check_inbox_title": "Check your inbox",
    "forgot.check_inbox_body": "If an account with that email exists, we've sent a reset link. The link expires in 60 minutes.",
    "forgot.back_to_login": "Back to sign in",
    // Reset password page
    "reset.eyebrow": "Set New Password",
    "reset.title": "Choose a new password",
    "reset.new_password": "New Password",
    "reset.confirm_password": "Confirm Password",
    "reset.submit": "Update Password",
    "reset.mismatch": "Passwords do not match",
    "reset.too_short": "Password must be at least 8 characters",
    "reset.failed": "Reset failed",
    "reset.success_title": "Password updated",
    "reset.success_body": "You can now sign in with your new password. Redirecting…",
    "reset.invalid_link": "This reset link is invalid or expired.",
    "reset.request_new": "Request a new link",
    "auth.forgot_password": "Forgot password?",
    "auth.dont_have_account": "Don't have an account?",
    "auth.have_account": "Already have an account?",
    "auth.sign_up": "Sign Up",
    // Session security (last active + sign out everywhere)
    "session.last_active": "Last active",
    "session.never": "just now",
    "session.minutes_ago": "{n} min ago",
    "session.hours_ago": "{n} h ago",
    "session.signed_out_everywhere": "Signed out of every device.",
    "session.signed_out_other_tab": "You signed out in another tab.",
    "session.signed_out_inactivity": "You've been signed out due to inactivity.",
    "session.warning_title": "Session expiring soon",
    "session.warning_body": "You'll be signed out in 30 seconds due to inactivity.",
    "session.stay_signed_in": "Stay signed in",
    "session.sign_out_everywhere": "Sign out everywhere",
    "session.sign_out_everywhere_hint": "Signs you out of every browser and device where this account is currently logged in.",
    "session.sign_out_everywhere_confirm": "End every other session for this account? You'll be signed out here and will need to sign in again.",
    "session.recent_signins": "Recent sign-ins",
    "session.recent_signins_hint": "Devices that have signed in to your account in the last 30 days. Don't recognize one? Click 'Sign out everywhere' below.",
    "session.current_session": "Current session",
    "session.times_seen": "{n} sign-ins",
    "session.no_sessions": "No recent sign-ins recorded.",
    // Home page
    "home.hero.h1_a": "Numbers tell a story.",
    "home.hero.h1_b": "We help you write",
    "home.hero.h1_c": "a better one.",
    "home.hero.sub": "Bookkeeping, tax preparation, notary services, ITIN support, sales tax reporting, and Spanish translations — all in one trusted, bilingual platform.",
    "home.cta.schedule": "Schedule Appointment",
    "home.cta.intake": "Start Client Intake",
    "home.cta.upload": "Upload Documents",
    "home.cta.view_services": "View Services",
    "home.badge.bilingual": "Bilingual · EN / ES",
    "home.badge.secure": "Secure & confidential",
    "home.badge.ai": "AI-assisted bookkeeping",
    "home.services.eyebrow": "Our Services",
    "home.services.h2_a": "Seven services.",
    "home.services.h2_b": "One trusted partner.",
    "home.services.view_all": "View all services",
    "home.services.learn_more": "Learn more",
    "home.qr.eyebrow": "Scan · Start · Done",
    "home.qr.h2_a": "Scan a QR.",
    "home.qr.h2_b": "We'll take it from there.",
    "home.qr.sub": "From a flyer, a business card, or your phone — scan the code and land on a fast, mobile-friendly intake. We make it effortless to get started.",
    "home.qr.open": "Open Intake Page",
    "home.install.eyebrow": "Install the App",
    "home.install.h2_a": "Roman Numerals III,",
    "home.install.h2_b": "always one tap away.",
    "home.install.sub": "Install our app to your phone or desktop for instant access to your portal, appointments, documents, and Adelis AI — no app store needed.",
    "home.install.installed": "Installed on this device",
    "home.install.mobile.eyebrow": "iPhone · Android",
    "home.install.mobile.title": "Install on Mobile",
    "home.install.mobile.desc": "Add Roman Numerals III to your Home Screen for a full-screen, native-app experience with our branded splash on launch.",
    "home.install.mobile.cta": "Install on Mobile",
    "home.install.desktop.eyebrow": "Mac · Windows · Chromebook",
    "home.install.desktop.title": "Install on Desktop",
    "home.install.desktop.desc": "Pin Roman Numerals III to your dock or taskbar. Works in Chrome, Edge, Brave, and Opera — opens like a native desktop app.",
    "home.install.desktop.cta": "Install on Desktop",
    "home.install.desktop.cta_manual": "How to install",
    "home.install.desktop.step1": "Open romannumeralsiii.com in Chrome, Edge, Brave, or Opera.",
    "home.install.desktop.step2": "Click the install icon on the right side of the address bar (looks like a screen with a down arrow).",
    "home.install.desktop.step3": "Click Install. The app will open in its own window and appear in your dock, taskbar, or apps folder.",
    "home.install.ios.eyebrow": "How to install",
    "home.install.ios.title_ios": "Add to Home Screen on iPhone",
    "home.install.ios.title_desktop": "Install on your browser",
    "home.install.ios.step1": "Open this site in Safari (not Chrome or in-app browsers).",
    "home.install.ios.step2a": "Tap the",
    "home.install.ios.share": "Share",
    "home.install.ios.step2b": "icon at the bottom of Safari.",
    "home.install.ios.step3a": "Scroll and tap",
    "home.install.ios.add_to_home": "Add to Home Screen",
    "home.install.ios.step3b": ", then Add.",
    "home.install.ios.got_it": "Got it",
    "home.social.h2": "Follow our work",
    "home.social.sub": "Education, tips, and bilingual community content.",
    // Footer
    "footer.about": "Bookkeeping, tax preparation, notary services, ITIN support, sales tax reporting, and Spanish translations — all in one trusted platform.",
    "footer.call_to_schedule": "Call to schedule",
    "footer.bilingual_support": "Bilingual support — English & Español",
    "footer.navigate": "Navigate",
    "footer.qr_welcome": "QR Welcome",
    // Services page
    "services.eyebrow": "Services",
    "services.h1_a": "Solutions. Strategy.",
    "services.h1_b": "Structure. Success.",
    "services.intro": "Practical, bilingual support across the seven services your business and family actually need.",
    "services.docs_heading": "Documents you may need",
    "services.start_intake": "Start Intake",
    "services.schedule": "Schedule Appointment",
    "services.ask_question": "Ask a question",
    "services.label": "Service",
    // Contact page
    "contact.eyebrow": "Contact",
    "contact.h1_a": "Reach out.",
    "contact.h1_b": "We'll respond promptly.",
    "contact.intro": "Email, phone, or send a message below. We typically respond within 1 business day.",
    "contact.form.name": "Your name",
    "contact.form.email": "Your email",
    "contact.form.subject": "Subject (optional)",
    "contact.form.message": "How can we help?",
    "contact.form.send": "Send Message",
    "contact.success": "Thanks — we'll be in touch shortly.",
    "contact.detail.email": "Email",
    "contact.detail.phone": "Phone",
    "contact.detail.languages": "Languages",
    "contact.detail.languages_val": "English & Español",
    // Welcome page
    "welcome.eyebrow": "Welcome",
    "welcome.h1": "Glad you scanned.",
    "welcome.sub": "Pick what you came here for. Each option leads to a fast, mobile-friendly experience.",
    "welcome.intake": "Start Client Intake",
    "welcome.intake_sub": "Tell us about your business or service need.",
    "welcome.book": "Book an Appointment",
    "welcome.book_sub": "Pick a time that works for you.",
    "welcome.portal": "Client Portal",
    "welcome.portal_sub": "Upload docs, view reports, pay invoices.",
    "welcome.refer": "Refer a Friend",
    "welcome.refer_sub": "Send us someone who needs us — thank you!",
    "welcome.h2_a": "Welcome to Roman Numerals III.",
    "welcome.h2_b": "How can we help you today?",
    "welcome.opt.bookkeeping": "I need bookkeeping help",
    "welcome.opt.tax_prep": "I need tax preparation",
    "welcome.opt.notary": "I need a notary",
    "welcome.opt.itin": "I need ITIN assistance",
    "welcome.opt.sales_tax": "I need sales tax reporting",
    "welcome.opt.translation": "I need Spanish translation",
    "welcome.opt.consultation": "I want to schedule a consultation",
    "welcome.back_home": "Back to home",
    // Resources page
    "resources.eyebrow": "Resources",
    "resources.h1": "Tools, guides, and bilingual help — free.",
    "resources.intro": "Curated content from our practice and the IRS, designed to help you understand the numbers behind your business.",
    // Appointments page
    "appts.eyebrow": "Appointments",
    "appts.h1_a": "Book a time.",
    "appts.h1_b": "We'll be ready.",
    "appts.intro": "Choose the service and a time that works for you. We'll confirm via email within a few hours.",
    "appts.form.service": "Service",
    "appts.form.date": "Preferred date",
    "appts.form.time": "Preferred time",
    "appts.form.notes": "Notes (optional)",
    "appts.form.submit": "Request Appointment",
    "appts.success": "Request received — we'll confirm by email.",
    // About page
    "about.eyebrow": "About",
    "about.h1_a": "A family-run firm",
    "about.h1_b": "built on numbers and trust.",
    // Intake & Portal — minimal core labels
    "intake.eyebrow": "Client Intake",
    "intake.h1": "Tell us about your needs",
    "intake.intro": "Share a few details so we can prep before our first conversation. Takes about 3 minutes.",
    "intake.consent": "I agree to be contacted about my request.",
    "intake.submit": "Submit Intake",
    "intake.success": "Thank you — we'll be in touch.",
    "portal.eyebrow": "Admin Portal",
    "portal.invite_only.heading": "Invite-only access",
    "portal.invite_only.body": "New clients are onboarded by Adelis directly. Complete your intake or book a consultation, and you'll receive a personalized set-up link by email.",
    "portal.invite_only.cta_intake": "Start intake",
    "portal.invite_only.cta_book": "Book a call",
    "portal.welcome": "Welcome",
    "portal.upload_docs": "Upload Documents",
    "portal.your_appts": "Your Appointments",
    "portal.your_docs": "Your Documents",
    "portal.reports": "View Reports",
    "portal.no_data": "Nothing here yet.",
    // Service body content (descriptions + docs)
    "service.bookkeeping.short": "Organized books, every month.",
    "service.bookkeeping.desc": "Monthly reconciliation, categorization, and clean financial records that help you make confident decisions.",
    "service.bookkeeping.docs": "Bank statements (PDF/CSV)|Receipts|Last year's books (if any)|Chart of accounts",
    "service.tax_prep.short": "Individuals, families, and small businesses.",
    "service.tax_prep.desc": "Accurate, on-time tax preparation with deduction guidance for personal and business returns.",
    "service.tax_prep.docs": "W-2 / 1099 forms|Prior year return|ID & SSN/ITIN|Receipts & deduction records",
    "service.sales_tax.short": "Filed correctly. Filed on time.",
    "service.sales_tax.desc": "Monthly or quarterly sales tax filings tailored to your jurisdiction and business structure.",
    "service.sales_tax.docs": "Sales reports|POS export|Sales tax permit|Exempt sales documentation",
    "service.notary.short": "In-person & mobile notarization.",
    "service.notary.desc": "Acknowledgments, jurats, affidavits, and personal documents — quickly and professionally.",
    "service.notary.docs": "Valid government-issued ID|Document to be notarized (unsigned)",
    "service.loan_signing.short": "Certified loan document signings.",
    "service.loan_signing.desc": "Professional loan document signings for refinances, purchases, HELOCs, and reverse mortgages.",
    "service.loan_signing.docs": "Loan document package|Valid ID for all signers",
    "service.itin.short": "Apply or renew your ITIN.",
    "service.itin.desc": "Certified Acceptance Agent support so you don't have to mail original documents to the IRS.",
    "service.itin.docs": "Passport or supporting ID documents|W-7 form (we help complete)|Tax return (if filing)",
    "service.translation.short": "Bilingual documents, done right.",
    "service.translation.desc": "Spanish-English translation of personal, legal, and business documents with a certificate of accuracy.",
    "service.translation.docs": "Source document (any format)",
    "appts.consultation": "General Consultation",
    "appts.notes": "Notes",
    "appts.choose_date": "Choose a date",
    "appts.available_times": "Available times",
    "appts.no_slots": "No slots available for this date.",
    "appts.calendar_soon": "Calendar sync (Google Calendar) coming soon.",
    "appts.lang_label": "Preferred Language",
    "appts.full_name": "Full Name",
    "appts.phone": "Phone",
    "appts.email": "Email",
    "appts.notes_optional": "Notes (optional)",
    "appts.select_time": "Please select a time",
    "appts.requested": "Appointment Requested",
    "appts.requested_body_a": "We received your request for",
    "appts.requested_body_b": "on",
    "appts.requested_body_c": "at",
    "appts.requested_body_d": "We'll confirm shortly via email.",
    "appts.book_another": "Book another",
    "appts.toast_ok": "Appointment requested! You'll receive a confirmation soon.",
    "appts.toast_fail": "Failed to submit",
    // Intake page
    "intake.service.general": "General Client Intake",
    "intake.full_name": "Full Name",
    "intake.phone": "Phone",
    "intake.email": "Email",
    "intake.lang_label": "Preferred Language",
    "intake.address": "Address",
    "intake.urgency": "Urgency",
    "intake.urgency.low": "Low — no rush",
    "intake.urgency.normal": "Normal",
    "intake.urgency.high": "High — please prioritize",
    "intake.description": "Description of your need",
    "intake.attach_docs": "Attach documents",
    "intake.files_help": "Up to 5 files, 10 MB each. PDF, images, CSV, TXT, Word, Excel, PowerPoint. Executables, scripts, torrents and archives are blocked.",
    "intake.files_max": "Max 5 files per intake.",
    "intake.file_blocked_type": "Blocked file type",
    "intake.file_too_large": "File exceeds 10 MB",
    "intake.file_ready": "file ready to upload",
    "intake.files_ready": "files ready to upload",
    "intake.upload_failed": "Upload failed",
    "intake.login_tip_a": "Tip:",
    "intake.login_tip_b": "log in",
    "intake.login_tip_c": "to attach files to your intake.",
    "intake.consent_full": "I consent to Roman Numerals III contacting me about my request and processing the information I provided.",
    "intake.submitting": "Submitting…",
    "intake.confirm.thanks": "Thank you",
    "intake.confirm.body_a": "Your intake for",
    "intake.confirm.body_b": "has been received. Adelis will review and reach out shortly.",
    "intake.confirm.scan_title": "Files secured",
    "intake.confirm.scan_file_singular": "file was",
    "intake.confirm.scan_file_plural": "files were",
    "intake.confirm.scan_body": "virus-scanned by ClamAV before delivery to Adelis. Anything malicious is automatically blocked and deleted.",
    "intake.confirm.schedule": "Schedule Appointment",
    "intake.confirm.open_portal": "Open Client Portal",
    "intake.toast_consent": "Please confirm consent",
    "intake.toast_ok": "Intake submitted!",
    "intake.toast_fail": "Failed to submit",
    "intake.extra.business_name": "Business name",
    "intake.extra.months_behind": "How many months behind?",
    "intake.extra.filing_status": "Filing status (single/married/etc.)",
    "intake.extra.dependents": "Number of dependents",
    "intake.extra.state": "State(s) where you collect sales tax",
    "intake.extra.frequency": "Filing frequency (monthly/quarterly/annual)",
    "intake.extra.doc_type": "Type of document to notarize",
    "intake.extra.location": "Location preference (in-person/mobile)",
    "intake.extra.lender": "Lender / Title company",
    "intake.extra.signing_date": "Preferred signing date",
    "intake.extra.applicant_type": "New ITIN or Renewal?",
    "intake.extra.country": "Country of citizenship",
    "intake.extra.source_lang": "Source language",
    "intake.extra.target_lang": "Target language",
    // Portal dashboard
    "portal.dashboard.title": "Your dashboard",
    "portal.dashboard.intakes": "My Intakes",
    "portal.dashboard.no_intakes": "No intakes yet.",
    "portal.dashboard.appts": "Appointments",
    "portal.dashboard.no_appts": "No appointments yet.",
    "portal.dashboard.documents": "Documents",
    "portal.dashboard.upload": "Upload",
    "portal.upload.file_label": "File(s)",
    "portal.upload.selected": "selected",
    "portal.upload.account_label": "Account",
    "portal.upload.account_placeholder": "e.g. Chase Checking",
    "portal.upload.period_label": "Period",
    "portal.upload.period_none": "— any —",
    "portal.upload.type_label": "Type",
    "portal.upload.type.receipt": "Receipt",
    "portal.upload.type.bank_statement": "Statement",
    "portal.upload.type.invoice": "Invoice",
    "portal.upload.type.check": "Check",
    "portal.upload.type.other": "Other",
    "portal.upload.vendor_label": "Vendor",
    "portal.upload.vendor_placeholder": "e.g. Home Depot",
    "portal.upload.amount_label": "Amount ($)",
    "portal.upload.category_label": "Category",
    "portal.upload.category_other_placeholder": "Describe the category…",
    "portal.upload.notes_label": "Notes (optional)",
    "portal.upload.notes_placeholder": "Anything else useful for reconciliation",
    "portal.upload.cat.office_supplies": "Office Supplies",
    "portal.upload.cat.meals_entertainment": "Meals & Entertainment",
    "portal.upload.cat.fuel_auto": "Fuel & Auto",
    "portal.upload.cat.travel_lodging": "Travel & Lodging",
    "portal.upload.cat.utilities": "Utilities",
    "portal.upload.cat.software_subscriptions": "Software & Subscriptions",
    "portal.upload.cat.rent_lease": "Rent & Lease",
    "portal.upload.cat.insurance": "Insurance",
    "portal.upload.cat.marketing_advertising": "Marketing & Advertising",
    "portal.upload.cat.professional_services": "Professional Services",
    "portal.upload.cat.bank_card_fees": "Bank & Card Fees",
    "portal.upload.cat.cogs": "Cost of Goods Sold (COGS)",
    "portal.upload.cat.other": "Other",
    "portal.upload.subcategory_label": "Subcategory (optional)",
    "portal.upload.subcategory_placeholder": "e.g. Gas, Oil change, Client meal…",
    "portal.dashboard.messages": "Messages with Roman Numerals III",
    "portal.dashboard.write_message": "Write a message…",
    "portal.dashboard.send": "Send",
    "portal.dashboard.no_messages": "No messages yet.",
    "portal.dashboard.you": "You",
    "portal.dashboard.uploaded": "Uploaded",
    "portal.dashboard.upload_failed": "Upload failed",
    "portal.dashboard.msg_sent": "Message sent",
    "portal.dashboard.demo_hint": "Demo:",
    "portal.dashboard.loading": "Loading…",
    "portal.dashboard.auth_failed": "Authentication failed",
    "portal.dashboard.account_created": "Account created",
    "portal.security_strip.heading": "Your account is protected",
    "portal.security_strip.bcrypt": "Bcrypt Hashed",
    "portal.security_strip.two_fa": "2FA Ready",
    "portal.security_strip.clamav": "ClamAV Scanned",
    "portal.security_strip.encrypted_transit": "Encrypted Transit",
    "portal.security_strip.audit_log": "Audit Trail",
    "portal.security_strip.tagline": "Every login is logged, every session is encrypted end-to-end over HTTPS, and every password is hashed with bcrypt. Bilingual support · English & Español.",
    "portal.books.eyebrow": "My Books",
    "portal.books.headline_one": "Open financial reports",
    "portal.books.headline_many": "books · Open financial reports",
    "portal.books.sub": "Profit & Loss, Balance Sheet, Cash Flow, AR Aging — read-only access to your books.",
    // 2FA challenge screen
    "twofa.eyebrow": "Two-Factor Verification",
    "twofa.confirm_title": "Confirm it's you",
    "twofa.sent_to_a": "We sent a 6-digit code to",
    "twofa.sent_to_via_sms": "via SMS.",
    "twofa.sent_to_via_email": "via email.",
    "twofa.code_label": "Verification Code",
    "twofa.recovery_label": "Recovery Code",
    "twofa.recovery_placeholder": "One of your saved recovery codes",
    "twofa.verify_btn": "Verify & Sign In",
    "twofa.use_recovery_btn": "Use Recovery Code",
    "twofa.resend": "Resend code",
    "twofa.use_recovery_link": "Use a recovery code instead",
    "twofa.back_to_code": "Back to code entry",
    "twofa.cancel": "Cancel",
    "twofa.toast_sent": "We sent a code to",
    "twofa.toast_new_sent": "New code sent to",
    "twofa.toast_resend_failed": "Resend failed",
    "twofa.toast_invalid": "Invalid code",
    // 2FA settings panel
    "twofa.settings.eyebrow": "Two-Factor Authentication",
    "twofa.settings.enabled": "Enabled",
    "twofa.settings.not_enabled": "Not enabled",
    "twofa.settings.channel_label": "Channel:",
    "twofa.settings.recovery_remaining": "Recovery codes remaining:",
    "twofa.settings.required_warning": "2FA is required for admin accounts.",
    "twofa.settings.intro": "Add an extra step to your sign-in: after entering your password, we'll send a 6-digit code to your email or phone. You can sign in from anywhere as long as you have access to the channel you choose.",
    "twofa.settings.choose_channel": "Choose your channel",
    "twofa.settings.email": "Email",
    "twofa.settings.sms": "SMS",
    "twofa.settings.sms_disabled_hint": "(add phone first)",
    "twofa.settings.send_setup_code": "Send setup code",
    "twofa.settings.sending": "Sending…",
    "twofa.settings.loading": "Loading 2FA status…",
    "twofa.settings.confirm_setup_eyebrow": "Confirm 2FA Setup",
    "twofa.settings.confirm_setup_title": "Enter the code we sent",
    "twofa.settings.confirm_sent_to": "We sent a 6-digit code to",
    "twofa.settings.confirm_btn": "Confirm & Enable 2FA",
    "twofa.settings.disable_link": "Disable 2FA",
    "twofa.settings.disable_hint": "Confirm your password to disable 2FA.",
    "twofa.settings.your_password": "Your password",
    "twofa.settings.disable_btn": "Disable 2FA",
    "twofa.settings.toast_enabled": "2FA enabled successfully",
    "twofa.settings.toast_disabled": "2FA disabled",
    "twofa.settings.toast_setup_failed": "Setup failed",
    "twofa.settings.toast_disable_failed": "Could not disable",
    "twofa.settings.toast_code_sent": "Code sent to",
    // Recovery codes display
    "twofa.recovery.eyebrow": "Recovery Codes",
    "twofa.recovery.title": "Save these in a safe place",
    "twofa.recovery.body_a": "These one-time codes will let you sign in if you lose access to your verification channel.",
    "twofa.recovery.body_b": "You will not see them again.",
    "twofa.recovery.body_c": "Save them now.",
    "twofa.recovery.copy_all": "Copy all codes",
    "twofa.recovery.done": "I've saved them",
    "twofa.recovery.toast_copied": "Recovery codes copied to clipboard",
    // Account security wrapper
    "portal.security.heading": "Account Security",
    // Client activity feed
    "portal.activity.eyebrow": "Recent Activity (last 30 days)",
    "portal.activity.title": "Your sign-in history",
    "portal.activity.loading": "Loading…",
    "portal.activity.empty": "No recorded activity yet.",
    "portal.activity.ip_label": "IP:",
    // About page
    "about.meet": "Meet Adelis",
    "about.intro_1": "Roman Numerals III was created to help individuals, families, entrepreneurs, and small business owners better understand their numbers, organize their records, and make smarter financial decisions.",
    "about.intro_2_a": "Led by",
    "about.intro_2_b": ", Roman Numerals III provides practical support in bookkeeping, tax preparation, sales tax reporting, notary services, signing agent services, ITIN acceptance support, and Spanish translations.",
    "about.journey.eyebrow": "The Journey",
    "about.journey.h2_a": "How Roman Numerals III",
    "about.journey.h2_b": "came to be.",
    "about.journey.sub": "Click below to read Adelis' story — the people, the lessons, and the moment that turned a calling into a firm.",
    "about.journey.title": "Adelis Roman — Founder of Roman Numerals III",
    "about.journey.founder": "Founder · Roman Numerals III",
    "about.journey.quote_1": "I have always believed that numbers tell a story. My job is to help people understand the one their numbers are telling — and write a better next chapter.",
    "about.journey.p1": "Adelis Roman is the Founder of Roman Numerals III, a premier financial services firm built on the core pillars of education, deep-rooted trust, and strategic partnership. With a diverse professional background spanning banking at Wells Fargo, corporate accounting, and specialized financial administration, Adelis empowers individuals, families, and business owners to navigate complex financial landscapes with absolute confidence.",
    "about.journey.p2": "The foundation of Roman Numerals III is profoundly connected to her personal history and core values. Born in Puerto Rico, raised in New York, and established in Florida since 2013, Adelis chose a company name that honors her family heritage as the third oldest of seven siblings. This deep commitment to legacy drives her professional mission: to build lasting, multi-generational relationships and deliver impactful, community-focused solutions.",
    "about.journey.p3": "Today, her comprehensive suite of services includes expert tax preparation, full-charge bookkeeping, IRS Acceptance Agent services, Certified Notary Signing Agent services, and professional document translation. Demonstrating an unwavering commitment to industry excellence, Adelis is currently pursuing her Enrolled Agent (EA) credential — the highest federally recognized tax designation awarded by the Internal Revenue Service. She maintains an aggressive schedule of continuing education, ensuring her clients benefit from proactive insights regarding changing tax laws, IRS regulations, and modern accounting practices.",
    "about.journey.p3b": "As a passionate advocate for economic empowerment, Adelis dedicates her expertise to bridging financial literacy gaps. She is particularly devoted to helping the growing Latino community master the financial system, resolve complex tax matters, and build sustainable wealth.",
    "about.journey.quote_2": "The people I serve are not transactions. They are mothers, fathers, business owners, dreamers, and builders. They deserve someone in their corner who understands both the spreadsheet and the story behind it.",
    "about.journey.p4_a": "Roman Numerals III is the result of that calling: a modern, bilingual, AI-assisted firm built to make financial and administrative work feel less heavy and more hopeful. The name itself is a quiet promise — the strength of a long tradition (Roman numerals) paired with the three pillars that guide every engagement:",
    "about.journey.p4_b": "Solutions. Strategy. Structure.",
    "about.journey.p4_c": "All in service of the fourth pillar — your",
    "about.journey.p4_d": "Success.",
    "about.journey.p5": "Based in Jacksonville, Florida, Adelis balances her executive leadership with her roles as a proud mother of two adult children and a grandmother. At Roman Numerals III, her mandate remains absolute: to educate, empower, and serve with uncompromising integrity, delivering true financial peace of mind to every client she serves.",
    "about.journey.cta_schedule": "Schedule a conversation with Adelis",
    "about.journey.cta_services": "See how we can help",
    "about.cards.mission_title": "Our Mission",
    "about.cards.mission_body": "Simplify complicated financial and administrative processes while helping clients feel informed, prepared, and confident.",
    "about.cards.story_title": "Our Story",
    "about.cards.story_body": "Built on years of practical experience supporting families and small businesses through the moments that matter most.",
    "about.cards.why_title": "Why Roman Numerals III",
    "about.cards.why_body": "Professional service, bilingual support, modern technology, and AI-powered tools — a better client experience from first contact to final completion.",
    "about.bilingual.eyebrow": "Bilingual Support",
    "about.bilingual.h2_a": "English &",
    "about.bilingual.h2_b": "Español",
    "about.bilingual.body": "From intake to filing, every interaction is available in the language you're most comfortable with. Confidence comes from clarity.",
    "about.platform.title": "A modern, secure platform",
    "about.platform.body": "AI-assisted bookkeeping, secure document upload, and a client portal that keeps your records organized and accessible.",
    "about.platform.link": "Explore our services",
    "about.follow.title": "Follow Adelis & Roman Numerals III",
    "about.follow.sub": "Tips, education, and bilingual community content across socials.",
    // Resources page
    "resources.topics_h2": "Topics",
    "resources.media_h2": "Media · Coming Soon",
    "resources.placeholder": "Placeholder",
    "resources.topic.bookkeeping.title": "Bookkeeping Tips",
    "resources.topic.bookkeeping.body": "Build clean books from the start: categorization, monthly close, and avoiding the most common mistakes.",
    "resources.topic.tax.title": "Tax Tips",
    "resources.topic.tax.body": "Year-round strategies for individuals and small businesses to keep more of what you earn.",
    "resources.topic.sales_tax.title": "Sales Tax Tips",
    "resources.topic.sales_tax.body": "Filing frequencies, nexus, and how to avoid penalties — explained simply.",
    "resources.topic.itin.title": "ITIN Information",
    "resources.topic.itin.body": "Who needs one, what documents to gather, and how a Certified Acceptance Agent makes it easier.",
    "resources.topic.notary.title": "Notary Preparation",
    "resources.topic.notary.body": "What to bring, who needs to be present, and how to avoid a rejected notarization.",
    "resources.topic.spanish_biz.title": "Spanish Business Education",
    "resources.topic.spanish_biz.body": "Recursos en español para emprendedores y dueños de pequeñas empresas.",
    "resources.topic.personal.title": "Personal Finance Education",
    "resources.topic.personal.body": "Budgeting, saving, and using your numbers to plan a better future.",
    "resources.topic.ai.title": "AI for Small Business",
    "resources.topic.ai.body": "Practical ways to use modern tools — including AI — to run your business better.",
    "resources.media.reels.label": "Reels",
    "resources.media.reels.body": "Short, shareable tips on socials.",
    "resources.media.longform.label": "Long-form Videos",
    "resources.media.longform.body": "Deeper dives on YouTube.",
    "resources.media.podcast.label": "Podcast Episodes",
    "resources.media.podcast.body": "Conversations with founders and pros.",
    "resources.media.community.label": "Community Training",
    "resources.media.community.body": "Live and on-demand workshops.",
    // ============ ADMIN ============
    "admin.eyebrow": "Admin · Roman Numerals III",
    "admin.welcome": "Welcome",
    "admin.back_to_admin": "Back to Admin",
    "admin.nav.revenue": "Revenue",
    "admin.nav.tasks": "Tasks",
    "admin.nav.sms": "SMS",
    "admin.nav.email": "Email",
    "admin.nav.referrals": "Referrals",
    "admin.nav.leads": "Leads",
    "admin.nav.activity": "Activity",
    "admin.nav.back_office": "Back Office",
    "admin.stats.clients": "Clients",
    "admin.stats.books": "Client Books",
    "admin.stats.new_leads": "New Leads",
    "admin.stats.intakes": "Intakes",
    "admin.stats.upcoming_appts": "Upcoming Appts",
    "admin.stats.open_tasks": "Open Tasks",
    "admin.stats.overdue": "overdue",
    "admin.col.date": "Date", "admin.col.time": "Time", "admin.col.client": "Client",
    "admin.col.service": "Service", "admin.col.urgency": "Urgency", "admin.col.status": "Status",
    "admin.col.actions": "Actions", "admin.col.name": "Name", "admin.col.email": "Email",
    "admin.col.phone": "Phone", "admin.col.lang": "Lang", "admin.col.joined": "Joined",
    "admin.intakes.heading": "Intake Submissions", "admin.intakes.empty": "No intakes yet.",
    "admin.appts.heading": "Upcoming Appointments", "admin.appts.empty": "No appointments yet.",
    "admin.appts.approve": "Approve", "admin.appts.cancel": "Cancel",
    "admin.appt_status.pending": "Pending", "admin.appt_status.approved": "Approved",
    "admin.appt_status.rescheduled": "Rescheduled", "admin.appt_status.cancelled": "Cancelled",
    "admin.appt_status.completed": "Completed",
    "admin.clients.heading": "Clients", "admin.clients.hint": "Click any row to open internal notes & status",
    "admin.clients.empty": "No clients yet.",
    "admin.documents.heading": "Recent Documents", "admin.documents.download": "Download",
    "admin.documents.empty": "No documents uploaded yet.",
    "admin.documents.edit": "Edit",
    "admin.doc_edit.heading": "Amend Document",
    "admin.doc_edit.save": "Save Changes",
    "admin.doc_edit.cancel": "Cancel",
    "admin.doc_edit.none": "none",
    "admin.doc_edit.saved": "Metadata updated",
    "admin.doc_edit.failed": "Update failed",
    "admin.messages.heading": "Client Messages",
    "admin.messages.refresh": "Refresh",
    "admin.messages.empty": "No client messages yet.",
    "admin.messages.reply_placeholder": "Type your reply… the client will get an instant email.",
    "admin.messages.send_reply": "Send Reply",
    "admin.messages.reply_sent": "Reply sent — client emailed.",
    "admin.security.heading": "Account Security",
    "admin.scan.heading": "Virus Scan Status",
    "admin.scan.refresh": "Refresh",
    "admin.scan.engine": "Engine",
    "admin.scan.engine_ok": "Online",
    "admin.scan.engine_offline": "Offline",
    "admin.scan.latency": "Latency",
    "admin.scan.clean": "Scanned Clean",
    "admin.scan.quarantined": "In Quarantine",
    "admin.scan.blocked": "Blocked (30d)",
    "admin.scan.total": "Total Files",
    "admin.scan.recent_heading": "Recent malware blocks",
    "admin.scan.recent_empty": "No malicious uploads detected. ClamAV is watching every file.",
    "admin.scan.col_when": "When",
    "admin.scan.col_signature": "Signature",
    "admin.scan.col_filename": "Filename",
    "admin.scan.col_uploader": "Uploader",
    "admin.scan.col_ip": "IP",
    "admin.scan.anonymous": "anonymous",
    "admin.toast.status_updated": "Status updated",
    "admin.toast.updated": "Updated",
    "admin.toast.failed": "Failed",
    // -- Tasks --
    "admin.tasks.heading": "Tasks", "admin.tasks.center_eyebrow": "Admin · Task Center",
    "admin.tasks.new_task": "New Task", "admin.tasks.edit_task": "Edit Task",
    "admin.tasks.create_task": "Create Task", "admin.tasks.save_changes": "Save Changes",
    "admin.tasks.add": "Add", "admin.tasks.move_to": "Move to",
    "admin.tasks.overdue_caps": "OVERDUE",
    "admin.tasks.cycle_priority_title": "Click to cycle priority",
    "admin.tasks.delete_title": "Delete task",
    "admin.tasks.confirm_delete": "Delete",
    "admin.tasks.empty_column": "No tasks here.",
    "admin.tasks.search_placeholder": "Title or description…",
    "admin.tasks.all_priorities": "All Priorities",
    "admin.tasks.overdue_only": "Overdue only",
    "admin.tasks.quick_add_placeholder": "Type a quick task title here, then press Enter or click Add →",
    "admin.tasks.quick_add_disabled_title": "Type a task title first",
    "admin.tasks.status.pending": "Pending", "admin.tasks.status.in_progress": "In Progress", "admin.tasks.status.complete": "Complete",
    "admin.tasks.priority.low": "Low", "admin.tasks.priority.normal": "Normal", "admin.tasks.priority.high": "High", "admin.tasks.priority.urgent": "Urgent",
    "admin.tasks.field.title": "Title", "admin.tasks.field.title_placeholder": "e.g. Call client about Q4 review",
    "admin.tasks.field.description": "Description", "admin.tasks.field.priority": "Priority", "admin.tasks.field.due_date": "Due Date",
    "admin.tasks.stat.total": "Total", "admin.tasks.stat.overdue": "Overdue",
    "admin.tasks.toast.title_required": "Title is required",
    "admin.tasks.toast.created": "Task created", "admin.tasks.toast.updated": "Task updated",
    "admin.tasks.toast.save_failed": "Save failed", "admin.tasks.toast.load_failed": "Failed to load tasks",
    "admin.tasks.toast.update_failed": "Failed to update", "admin.tasks.toast.delete_failed": "Failed to delete",
    "admin.tasks.toast.deleted": "Task deleted", "admin.tasks.toast.added": "Task added",
    // -- Activity --
    "admin.activity.eyebrow": "Security", "admin.activity.heading": "Activity Log",
    "admin.activity.description": "Every authentication and sensitive operation, with IP address + user agent. Auto-purged after 180 days.",
    "admin.activity.refresh": "Refresh",
    "admin.activity.stat.successful": "Successful logins", "admin.activity.stat.failed": "Failed logins",
    "admin.activity.stat.locked": "Currently locked", "admin.activity.lockouts_active": "brute-force lockouts active",
    "admin.activity.last_days": "last {n} days", "admin.activity.top_action": "Top action",
    "admin.activity.events_count": "{n} events",
    "admin.activity.filter.action": "Action", "admin.activity.filter.last": "Last",
    "admin.activity.filter.search": "Search by email / IP", "admin.activity.search_placeholder": "user@example.com or 1.2.3.4",
    "admin.activity.range.24h": "24 hours", "admin.activity.range.7d": "7 days",
    "admin.activity.range.30d": "30 days", "admin.activity.range.90d": "90 days",
    "admin.activity.showing": "Showing {n} of {total} events", "admin.activity.empty": "No events match the current filter.",
    "admin.activity.col.when": "When", "admin.activity.col.action": "Action", "admin.activity.col.user": "User",
    "admin.activity.col.ip": "IP Address", "admin.activity.col.user_agent": "User Agent", "admin.activity.col.details": "Details",
    "admin.activity.action.all": "All actions",
    "admin.activity.action.login_success": "Login success",
    "admin.activity.action.login_failed": "Login failed",
    "admin.activity.action.login_2fa_required": "2FA challenge issued",
    "admin.activity.action.login_2fa_verified": "2FA verified",
    "admin.activity.action.login_2fa_failed": "2FA failed",
    "admin.activity.action.logout": "Logout",
    "admin.activity.action.register": "Registration",
    "admin.activity.action.2fa_setup_completed": "2FA enabled",
    "admin.activity.action.2fa_disabled": "2FA disabled",
    "admin.activity.action.2fa_recovery_used": "Recovery code used",
    // -- Revenue --
    "admin.revenue.eyebrow": "Admin · Revenue", "admin.revenue.heading": "Revenue Dashboard",
    "admin.revenue.as_of": "As of", "admin.revenue.vs_last_month": "vs last month",
    "admin.revenue.kpi.total_clients": "Total Clients", "admin.revenue.kpi.monthly": "Monthly Revenue",
    "admin.revenue.kpi.pending": "Pending Revenue", "admin.revenue.kpi.pending_sub": "Unpaid invoice balances",
    "admin.revenue.kpi.collected_mtd": "Collected MTD", "admin.revenue.kpi.ytd": "YTD",
    "admin.revenue.kpi.upcoming_appts": "Upcoming Appts", "admin.revenue.kpi.total_lower": "total",
    "admin.revenue.kpi.lead_conversion": "Lead Conversion", "admin.revenue.leads_lower": "leads",
    "admin.revenue.kpi.alltime": "All-Time Revenue",
    "admin.revenue.trend.eyebrow": "12-Month Trend", "admin.revenue.trend.heading": "Revenue Over Time",
    "admin.revenue.legend.billed": "Billed", "admin.revenue.legend.collected": "Collected",
    "admin.revenue.legend.total": "Leads", "admin.revenue.legend.converted": "Converted",
    "admin.revenue.by_service.eyebrow": "By Service", "admin.revenue.by_service.heading": "Revenue by Service", "admin.revenue.by_service.empty": "No invoices yet.",
    "admin.revenue.by_source.eyebrow": "By Source", "admin.revenue.by_source.heading": "Lead Conversion", "admin.revenue.by_source.empty": "No leads yet.",
    "admin.revenue.billed_lower": "billed", "admin.revenue.collected_lower": "collected", "admin.revenue.invoices_lower": "invoices",
    "admin.revenue.drill_in": "Drill in", "admin.revenue.drill.backoffice": "Back Office (invoices & books)",
    "admin.revenue.toast.load_failed": "Failed to load revenue data",
    // -- Referrals --
    "admin.referrals.eyebrow": "Admin · Referrals", "admin.referrals.heading": "Referral System",
    "admin.referrals.new": "New Referral", "admin.referrals.log": "Log Referral",
    "admin.referrals.top_referrers": "Top Referrers", "admin.referrals.search_placeholder": "Name or email…",
    "admin.referrals.all_statuses": "All", "admin.referrals.empty": "No referrals yet. Click New Referral to log your first one.",
    "admin.referrals.unknown": "Unknown", "admin.referrals.issue": "Issue", "admin.referrals.mark_paid": "Paid Out",
    "admin.referrals.confirm_delete": "Delete this referral?",
    "admin.referrals.referrals_lower": "referrals", "admin.referrals.converted_lower": "converted",
    "admin.referrals.col.referrer_referred": "Referrer → Referred",
    "admin.referrals.col.source": "Source", "admin.referrals.col.reward": "Reward", "admin.referrals.col.created": "Created",
    "admin.referrals.stat.total": "Total Referrals", "admin.referrals.stat.rewards_pending": "Rewards Pending", "admin.referrals.stat.rewards_owed": "Rewards Owed",
    "admin.referrals.status.pending": "Pending", "admin.referrals.status.contacted": "Contacted",
    "admin.referrals.status.converted": "Converted", "admin.referrals.status.rewarded": "Rewarded", "admin.referrals.status.declined": "Declined",
    "admin.referrals.source.word_of_mouth": "Word of Mouth", "admin.referrals.source.email": "Email",
    "admin.referrals.source.text": "Text/SMS", "admin.referrals.source.qr": "QR Code",
    "admin.referrals.source.social": "Social Media", "admin.referrals.source.other": "Other",
    "admin.referrals.reward_type.discount_15": "15% Discount", "admin.referrals.reward_type.discount_25": "25% Discount",
    "admin.referrals.reward_type.credit_50": "$50 Credit", "admin.referrals.reward_type.credit_100": "$100 Credit",
    "admin.referrals.reward_type.free_notary": "Free Notary", "admin.referrals.reward_type.free_consult": "Free Consultation",
    "admin.referrals.reward_type.custom": "Custom",
    "admin.referrals.reward_status.pending": "Reward Pending", "admin.referrals.reward_status.issued": "Reward Issued", "admin.referrals.reward_status.paid_out": "Paid Out",
    "admin.referrals.form.referrer_client": "Referrer (existing client)", "admin.referrals.form.referrer_none": "— Not a client (enter name/email below) —",
    "admin.referrals.form.referrer_name": "Referrer name", "admin.referrals.form.referrer_email": "Referrer email",
    "admin.referrals.form.referred_party": "Referred Party", "admin.referrals.form.referred_name": "Full name *",
    "admin.referrals.form.reward": "Reward (optional)", "admin.referrals.form.no_reward": "— No reward —",
    "admin.referrals.form.reward_amount": "Dollar value (optional)", "admin.referrals.form.reward_note": "Reward note (e.g. apply to next invoice)",
    "admin.referrals.form.notes": "Notes",
    "admin.referrals.toast.referred_required": "Referred party name required", "admin.referrals.toast.logged": "Referral logged",
    "admin.referrals.toast.load_failed": "Failed to load referrals",
    "admin.referrals.toast.reward_issued": "Reward marked as issued",
    "admin.referrals.toast.paid_out": "Marked paid out", "admin.referrals.toast.deleted": "Deleted",
    // -- SMS --
    "admin.sms.eyebrow": "Admin · SMS Workflows", "admin.sms.heading": "SMS Automation",
    "admin.sms.live": "Live", "admin.sms.disabled": "Disabled",
    "admin.sms.twilio_live": "Twilio Live", "admin.sms.twilio_mocked": "Mocked · Add Twilio creds to go live",
    "admin.sms.twilio_not_configured": "Twilio not configured — test will render the message but won't actually send (logged to backend console).",
    "admin.sms.stat.total": "Total Workflows", "admin.sms.stat.enabled": "Enabled", "admin.sms.stat.disabled": "Disabled", "admin.sms.stat.runs": "Test Runs",
    "admin.sms.placeholders": "Placeholders", "admin.sms.click_chip": "Click a chip to insert into the {lang} template below.",
    "admin.sms.template": "Template", "admin.sms.chars": "chars", "admin.sms.segments": "SMS segments", "admin.sms.segment": "segment",
    "admin.sms.preview": "Preview", "admin.sms.preview_en": "Preview EN", "admin.sms.preview_es": "Preview ES",
    "admin.sms.send_test": "Send Test", "admin.sms.send": "Send",
    "admin.sms.toggle_enable": "Enable workflow", "admin.sms.toggle_disable": "Disable workflow",
    "admin.sms.toast.saved": "Workflow saved", "admin.sms.toast.enabled": "Workflow enabled", "admin.sms.toast.disabled": "Workflow disabled",
    "admin.sms.toast.load_failed": "Failed to load workflows", "admin.sms.toast.preview_failed": "Preview failed",
    "admin.sms.toast.enter_phone": "Enter a phone number", "admin.sms.toast.send_failed": "Send failed",
    "admin.sms.toast.test_mocked": "Test SMS rendered (MOCKED — Twilio not configured). Body printed to backend log.",
    "admin.sms.toast.test_sent": "Test SMS sent to",
    // -- Email --
    "admin.email.eyebrow": "Admin · Email Campaigns", "admin.email.heading": "Email Automation",
    "admin.email.resend_live": "Resend Live", "admin.email.resend_mocked": "Mocked · Add Resend key to go live",
    "admin.email.resend_not_configured": "Resend not configured — test will render the email but won't deliver (logged to backend).",
    "admin.email.stat.campaigns": "Campaigns", "admin.email.stat.templates": "Email Templates",
    "admin.email.step": "Step", "admin.email.day": "Day",
    "admin.email.series": "Series", "admin.email.steps": "steps", "admin.email.broadcast": "Broadcast",
    "admin.email.subject": "Subject", "admin.email.body_html": "Body · HTML",
    "admin.email.html_supported": "raw HTML supported", "admin.email.close": "Close",
    "admin.email.save_all": "Save All Steps", "admin.email.hide_editor": "Hide email editor",
    "admin.email.edit_emails_prefix": "Edit", "admin.email.emails_singular": "email", "admin.email.emails_plural": "emails",
    "admin.email.toggle_enable": "Enable campaign", "admin.email.toggle_disable": "Disable campaign",
    "admin.email.toast.saved": "Campaign saved", "admin.email.toast.enabled": "Campaign enabled", "admin.email.toast.disabled": "Campaign disabled",
    "admin.email.toast.load_failed": "Failed to load campaigns",
    "admin.email.toast.enter_email": "Enter an email", "admin.email.toast.test_mocked": "Rendered (MOCKED — Resend not configured). Subject:",
    "admin.email.toast.test_sent_prefix": "Sent to",
    // -- Client Drawer --
    "admin.client.drawer_eyebrow": "Client · Internal Notes",
    "admin.client.no_status": "No status",
    "admin.client.status.active": "Active", "admin.client.status.waiting_on_documents": "Waiting on Documents",
    "admin.client.status.tax_return_pending": "Tax Return Pending", "admin.client.status.follow_up_scheduled": "Follow-Up Scheduled",
    "admin.client.status.in_progress": "In Progress", "admin.client.status.on_hold": "On Hold",
    "admin.client.status.completed": "Completed", "admin.client.status.archived": "Archived",
    "admin.client.set_internal_status": "Set Internal Status", "admin.client.quick_jot": "Quick Jot",
    "admin.client.add_detailed_note": "Add Detailed Note", "admin.client.append_note": "Append Note",
    "admin.client.timeline": "Timeline", "admin.client.entries": "entries",
    "admin.client.timeline_empty": "No notes yet. Use the Quick Jot buttons above for one-click entries.",
    "admin.client.note_placeholder": "Spoke with client about Q4 review; requested 2024 1099-NEC by end of week.",
    "admin.client.tag_label_prefix": "tag", "admin.client.origin": "Origin",
    "admin.client.referral_link": "Personal Referral Link",
    "admin.client.referral_hint": "Share this link via email, text, or QR. Any referrals come back tagged to {name} automatically.",
    "admin.client.this_client": "this client", "admin.client.copy": "Copy",
    "admin.client.send.heading": "Send To Client",
    "admin.client.send.hint": "Both actions email {name} instantly.",
    "admin.client.send.upload_heading": "Upload Document For Client",
    "admin.client.send.upload_btn": "Share With Client",
    "admin.client.send.doc_sent": "Document shared — client emailed.",
    "admin.client.send.doc_failed": "Upload failed",
    "admin.client.send.too_large": "File too large (max 15MB)",
    "admin.client.send.msg_heading": "Send Message",
    "admin.client.send.msg_placeholder": "Type a quick message — they'll get an instant email and see it in their portal.",
    "admin.client.send.msg_btn": "Send Message",
    "admin.client.send.msg_sent": "Message sent — client emailed.",
    "admin.client.send.msg_failed": "Send failed",
    "admin.client.send.cat.general": "General",
    "admin.client.send.cat.tax_return": "Tax Return",
    "admin.client.send.cat.bookkeeping": "Bookkeeping",
    "admin.client.send.cat.notary": "Notary",
    "admin.client.send.cat.itin": "ITIN",
    "admin.client.send.cat.translation": "Translation",
    "admin.client.send.cat.invoice": "Invoice",
    "admin.client.send.cat.statement": "Statement",
    "admin.client.send.cat.other": "Other",
    "admin.import.heading": "Import Bank/Card CSV",
    "admin.import.hint": "Upload a CSV of transactions to reconcile against uploaded receipts.",
    "admin.import.account_label": "Account label",
    "admin.import.account_placeholder": "e.g. Chase Business Checking",
    "admin.import.file_label": "CSV file",
    "admin.import.submit": "Import CSV",
    "admin.import.success": "Imported {n} transactions",
    "admin.import.failed": "Import failed",
    "admin.import.confirm_delete": "Delete this import and all {n} transactions?",
    "admin.import.deleted": "Batch deleted",
    "admin.import.delete_failed": "Delete failed",
    "admin.import.recent_batches": "Recent Imports",
    "admin.import.txns": "txns",
    "admin.import.txns_lower": "imported transactions",
    "admin.import.debits": "Debits",
    "admin.import.credits": "Credits",
    "admin.import.show_txns": "Show",
    "admin.import.hide_txns": "Hide",
    "admin.import.col.date": "Date",
    "admin.import.col.description": "Description",
    "admin.import.col.amount": "Amount",
    "admin.import.col.status": "Status",
    "admin.import.cleared_count": "Cleared",
    "admin.import.cleared_total": "Cleared total",
    "admin.import.suggest_matches": "Find receipt match",
    "admin.import.suggested_matches": "Suggested Receipt Matches",
    "admin.import.no_matches": "No confident matches found for this transaction.",
    "admin.import.strong": "STRONG",
    "admin.import.likely": "LIKELY",
    "admin.import.weak": "WEAK",
    "admin.import.confirm": "Confirm",
    "admin.import.unmatch": "Unlink current match",
    "admin.import.match_confirmed": "Match confirmed",
    "admin.import.match_failed": "Match failed",
    "admin.import.toggle_failed": "Update failed",
    "admin.import.auto_match_btn": "Auto-match latest",
    "admin.import.auto_matched": "Auto-matched {n} of {total} pending",
    "admin.import.auto_match_failed": "Auto-match failed",
    "admin.import.post_ready": "Ready to post to ledger",
    "admin.import.post_hint": "{n} cleared txns will land in your P&L / Balance Sheet",
    "admin.import.post_btn": "Post to Books",
    "admin.import.confirm_post": "Post {n} cleared transactions to the ledger?",
    "admin.import.posted": "Posted {n} to books",
    "admin.import.post_failed": "Post failed",
    "admin.import.no_cleared": "No cleared transactions to post",
    "admin.client.delete_note": "Delete note", "admin.client.confirm_delete_note": "Delete this note?",
    "admin.client.by": "by", "admin.client.invoices_on_file": "invoices on file",
    "admin.client.count.intakes": "Intakes", "admin.client.count.appts": "Appts",
    "admin.client.count.docs": "Docs", "admin.client.count.books": "Books",
    "admin.client.tag_label.called": "Called client", "admin.client.tag_label.emailed": "Emailed",
    "admin.client.tag_label.texted": "Texted", "admin.client.tag_label.met_in_person": "Met in person",
    "admin.client.tag_label.waiting_on_docs": "Waiting on documents", "admin.client.tag_label.documents_received": "Documents received",
    "admin.client.tag_label.tax_pending": "Tax return pending", "admin.client.tag_label.follow_up": "Follow-up scheduled",
    "admin.client.tag_label.payment_received": "Payment received", "admin.client.tag_label.payment_pending": "Payment pending",
    "admin.client.tag_body.called": "Called client", "admin.client.tag_body.emailed": "Emailed client",
    "admin.client.tag_body.texted": "Texted client", "admin.client.tag_body.met_in_person": "Met in person",
    "admin.client.tag_body.waiting_on_docs": "Waiting on documents", "admin.client.tag_body.documents_received": "Documents received",
    "admin.client.tag_body.tax_pending": "Tax return pending", "admin.client.tag_body.follow_up": "Follow-up scheduled",
    "admin.client.tag_body.payment_received": "Payment received", "admin.client.tag_body.payment_pending": "Payment pending",
    "admin.client.checklist.heading": "Document Checklists",
    "admin.client.checklist.empty": "No checklists yet. Pick a service template above to seed one with the required documents.",
    "admin.client.checklist_template.bookkeeping": "Bookkeeping", "admin.client.checklist_template.tax_prep": "Tax Prep",
    "admin.client.checklist_template.sales_tax": "Sales Tax", "admin.client.checklist_template.notary": "Notary",
    "admin.client.checklist_template.loan_signing": "Loan Signing", "admin.client.checklist_template.itin": "ITIN",
    "admin.client.checklist_template.translation": "Translation", "admin.client.checklist_template.custom": "Custom (empty)",
    "admin.client.item_status.missing": "MISSING", "admin.client.item_status.pending": "PENDING", "admin.client.item_status.received": "RECEIVED",
    "admin.client.item_status_cycle_title": "Click to cycle status",
    "admin.client.received_lower": "received", "admin.client.missing_lower": "missing",
    "admin.client.delete_checklist_title": "Delete checklist",
    "admin.client.confirm_delete_checklist": "Delete the",
    "admin.client.remove_item": "Remove item",
    "admin.client.add_custom_item": "Add custom item…",
    "admin.client.toast.checklist_load_failed": "Failed to load checklists",
    "admin.client.toast.checklist_added": "checklist added", "admin.client.toast.checklist_add_failed": "Failed to add checklist",
    "admin.client.toast.checklist_deleted": "Checklist deleted",
    "admin.client.toast.add_item_failed": "Failed to add item",
    "admin.client.toast.load_failed": "Failed to load client",
    "admin.client.toast.status_update_failed": "Failed to update status",
    "admin.client.toast.note_added": "Note added", "admin.client.toast.note_add_failed": "Failed to add note",
    "admin.client.toast.note_deleted": "Note deleted", "admin.client.toast.note_delete_failed": "Failed to delete",
    "admin.client.toast.referral_copied": "Referral link copied", "admin.client.toast.copy_failed": "Copy failed",
    // -- Back Office --
    "admin.bo.eyebrow": "Back Office", "admin.bo.heading": "Client Books",
    "admin.bo.subtitle": "Every client has their own set of books. Open one to start working.",
    "admin.bo.new_book": "New Book", "admin.bo.new_client_book": "New Client Book",
    "admin.bo.search_placeholder": "Search by client or book name…",
    "admin.bo.empty": "No books yet. Create one to get started.",
    "admin.bo.client_label": "Client", "admin.bo.currency_label": "Currency",
    "admin.bo.accounts_lower": "accounts", "admin.bo.txns_lower": "txns",
    "admin.bo.open": "Open", "admin.bo.all_books": "All Books",
    "admin.bo.add_account": "Add Account", "admin.bo.search_account": "Search account…",
    "admin.bo.filter_all": "All", "admin.bo.no_match": "No accounts match your filter.",
    "admin.bo.closing_packet": "Closing Packet", "admin.bo.closing_packet_full": "Monthly Closing Packet",
    "admin.bo.creating": "Creating…", "admin.bo.create_book": "Create Book",
    "admin.bo.field.book_name": "Book / Business Name", "admin.bo.field.book_name_placeholder": "e.g. Acme Bakery LLC",
    "admin.bo.field.entity_type": "Entity Type", "admin.bo.field.currency": "Currency",
    "admin.bo.field.fy_start": "Fiscal Year Start (Month)",
    "admin.bo.field.ein": "EIN (optional)", "admin.bo.field.link_client": "Link to Client (optional)",
    "admin.bo.field.not_linked": "— Not linked yet —",
    "admin.bo.field.type": "Type", "admin.bo.field.name_en": "Name (English)", "admin.bo.field.name_es": "Name (Español)",
    "admin.bo.account_type.asset": "Asset", "admin.bo.account_type.liability": "Liability",
    "admin.bo.account_type.equity": "Equity", "admin.bo.account_type.income": "Income",
    "admin.bo.account_type.cogs": "Cost of Goods Sold", "admin.bo.account_type.expense": "Expense",
    "admin.bo.col.code": "Code", "admin.bo.col.subtype": "Subtype",
    "admin.bo.col.balance": "Balance", "admin.bo.col.active": "Active",
    "admin.bo.toast.book_created": "Book created with full chart of accounts",
    "admin.bo.toast.create_failed": "Failed to create",
    "admin.bo.toast.account_added": "Account added",
  },
  es: {
    "nav.home": "Inicio",
    "nav.about": "Nosotros",
    "nav.services": "Servicios",
    "nav.appointments": "Citas",
    "nav.intake": "Admisión",
    "nav.portal": "Portal Administrativo",
    "nav.resources": "Recursos",
    "nav.contact": "Contacto",
    "nav.admin": "Administración",
    "nav.sign_out": "Cerrar Sesión",
    "nav.sign_in": "Iniciar Sesión",
    "common.save": "Guardar",
    "common.cancel": "Cancelar",
    "common.delete": "Eliminar",
    "common.edit": "Editar",
    "common.submit": "Enviar",
    "common.loading": "Cargando…",
    "common.search": "Buscar",
    "common.back": "Atrás",
    "common.continue": "Continuar",
    "common.confirm": "Confirmar",
    "common.yes": "Sí",
    "common.no": "No",
    "common.email": "Correo Electrónico",
    "common.phone": "Teléfono",
    "common.name": "Nombre",
    "common.full_name": "Nombre Completo",
    "common.message": "Mensaje",
    "common.required": "Obligatorio",
    "common.optional": "Opcional",
    "common.language": "Idioma",
    "common.english": "Inglés",
    "common.spanish": "Español",
    "footer.tagline": "Soluciones · Estrategia · Estructura",
    "footer.copyright": "Todos los derechos reservados.",
    "footer.powered_by": "Desarrollado por",
    "footer.contact": "Contacto",
    "footer.connect": "Conectar",
    "footer.services": "Servicios",
    "footer.legal": "Legal",
    "footer.privacy": "Política de Privacidad",
    "footer.terms": "Términos de Servicio",
    "service.bookkeeping": "Contabilidad",
    "service.tax_prep": "Preparación de Impuestos",
    "service.sales_tax": "Impuesto de Ventas",
    "service.notary": "Notaría",
    "service.loan_signing": "Firma de Préstamos",
    "service.itin": "ITIN",
    "service.translation": "Traducción",
    "cta.book_consult": "Agenda una Consulta Gratuita",
    "cta.get_started": "Comenzar",
    "cta.refer_friend": "Refiere un Amigo",
    "cta.upload_docs": "Subir Documentos",
    "cta.view_reports": "Ver Reportes",
    "cta.start_intake": "Iniciar Admisión",
    "auth.welcome_back": "Bienvenido de nuevo",
    "auth.password": "Contraseña",
    "auth.password_hint": "Mínimo 8 caracteres.",
    // Forgot password page
    "forgot.eyebrow": "Recuperación de Cuenta",
    "forgot.title": "Restablecer tu contraseña",
    "forgot.intro": "Ingresa el correo electrónico asociado a tu cuenta del portal. Te enviaremos un enlace para establecer una nueva contraseña.",
    "forgot.email_label": "Correo Electrónico",
    "forgot.submit": "Enviar Enlace de Restablecimiento",
    "forgot.check_inbox_title": "Revisa tu correo",
    "forgot.check_inbox_body": "Si existe una cuenta con ese correo, te enviamos un enlace de restablecimiento. El enlace expira en 60 minutos.",
    "forgot.back_to_login": "Volver a iniciar sesión",
    // Reset password page
    "reset.eyebrow": "Establecer Nueva Contraseña",
    "reset.title": "Elige una nueva contraseña",
    "reset.new_password": "Nueva Contraseña",
    "reset.confirm_password": "Confirmar Contraseña",
    "reset.submit": "Actualizar Contraseña",
    "reset.mismatch": "Las contraseñas no coinciden",
    "reset.too_short": "La contraseña debe tener al menos 8 caracteres",
    "reset.failed": "El restablecimiento falló",
    "reset.success_title": "Contraseña actualizada",
    "reset.success_body": "Ahora puedes iniciar sesión con tu nueva contraseña. Redirigiendo…",
    "reset.invalid_link": "Este enlace de restablecimiento es inválido o ha expirado.",
    "reset.request_new": "Solicitar un nuevo enlace",
    "auth.forgot_password": "¿Olvidaste tu contraseña?",
    "auth.dont_have_account": "¿No tienes una cuenta?",
    "auth.have_account": "¿Ya tienes una cuenta?",
    "auth.sign_up": "Crear Cuenta",
    // Session security (last active + sign out everywhere)
    "session.last_active": "Última actividad",
    "session.never": "ahora mismo",
    "session.minutes_ago": "hace {n} min",
    "session.hours_ago": "hace {n} h",
    "session.signed_out_everywhere": "Cerraste sesión en todos los dispositivos.",
    "session.signed_out_other_tab": "Has cerrado sesión en otra pestaña.",
    "session.signed_out_inactivity": "Has sido desconectado por inactividad.",
    "session.warning_title": "Tu sesión está por expirar",
    "session.warning_body": "Serás desconectado en 30 segundos por inactividad.",
    "session.stay_signed_in": "Mantener sesión",
    "session.sign_out_everywhere": "Cerrar sesión en todos lados",
    "session.sign_out_everywhere_hint": "Cierra tu sesión en todos los navegadores y dispositivos donde esta cuenta está conectada.",
    "session.sign_out_everywhere_confirm": "¿Cerrar todas las demás sesiones de esta cuenta? También cerrarás sesión aquí y deberás volver a entrar.",
    "session.recent_signins": "Inicios de sesión recientes",
    "session.recent_signins_hint": "Dispositivos que han iniciado sesión en tu cuenta en los últimos 30 días. ¿No reconoces alguno? Haz clic en 'Cerrar sesión en todos lados'.",
    "session.current_session": "Sesión actual",
    "session.times_seen": "{n} inicios de sesión",
    "session.no_sessions": "No hay inicios de sesión recientes registrados.",
    "home.hero.h1_a": "Los números cuentan una historia.",
    "home.hero.h1_b": "Te ayudamos a escribir",
    "home.hero.h1_c": "una mejor.",
    "home.hero.sub": "Contabilidad, preparación de impuestos, notaría, ITIN, reportes de impuesto de ventas y traducciones al español — todo en una plataforma bilingüe de confianza.",
    "home.cta.schedule": "Agendar Cita",
    "home.cta.intake": "Iniciar Admisión",
    "home.cta.upload": "Subir Documentos",
    "home.cta.view_services": "Ver Servicios",
    "home.badge.bilingual": "Bilingüe · EN / ES",
    "home.badge.secure": "Seguro y confidencial",
    "home.badge.ai": "Contabilidad asistida por IA",
    "home.services.eyebrow": "Nuestros Servicios",
    "home.services.h2_a": "Siete servicios.",
    "home.services.h2_b": "Un socio de confianza.",
    "home.services.view_all": "Ver todos los servicios",
    "home.services.learn_more": "Más información",
    "home.qr.eyebrow": "Escanea · Comienza · Listo",
    "home.qr.h2_a": "Escanea un QR.",
    "home.qr.h2_b": "Nosotros nos encargamos del resto.",
    "home.qr.sub": "Desde un flyer, una tarjeta de presentación o tu teléfono — escanea el código y abre una admisión rápida y móvil. Hacemos que empezar sea fácil.",
    "home.qr.open": "Abrir Página de Admisión",
    "home.install.eyebrow": "Instala la App",
    "home.install.h2_a": "Roman Numerals III,",
    "home.install.h2_b": "siempre a un toque.",
    "home.install.sub": "Instala nuestra aplicación en tu teléfono o computadora para acceso instantáneo a tu portal, citas, documentos y Adelis AI — sin necesidad de tienda de apps.",
    "home.install.installed": "Instalada en este dispositivo",
    "home.install.mobile.eyebrow": "iPhone · Android",
    "home.install.mobile.title": "Instalar en Móvil",
    "home.install.mobile.desc": "Agrega Roman Numerals III a tu pantalla de inicio para una experiencia de pantalla completa con nuestro splash de marca al iniciar.",
    "home.install.mobile.cta": "Instalar en Móvil",
    "home.install.desktop.eyebrow": "Mac · Windows · Chromebook",
    "home.install.desktop.title": "Instalar en Escritorio",
    "home.install.desktop.desc": "Ancla Roman Numerals III en tu dock o barra de tareas. Funciona en Chrome, Edge, Brave y Opera — se abre como una app nativa.",
    "home.install.desktop.cta": "Instalar en Escritorio",
    "home.install.desktop.cta_manual": "Cómo instalar",
    "home.install.desktop.step1": "Abre romannumeralsiii.com en Chrome, Edge, Brave u Opera.",
    "home.install.desktop.step2": "Haz clic en el icono de instalación al lado derecho de la barra de dirección (parece una pantalla con una flecha hacia abajo).",
    "home.install.desktop.step3": "Haz clic en Instalar. La app se abrirá en su propia ventana y aparecerá en tu dock, barra de tareas o carpeta de aplicaciones.",
    "home.install.ios.eyebrow": "Cómo instalar",
    "home.install.ios.title_ios": "Agregar a la Pantalla de Inicio en iPhone",
    "home.install.ios.title_desktop": "Instala en tu navegador",
    "home.install.ios.step1": "Abre este sitio en Safari (no en Chrome ni en navegadores dentro de apps).",
    "home.install.ios.step2a": "Toca el ícono",
    "home.install.ios.share": "Compartir",
    "home.install.ios.step2b": "en la parte inferior de Safari.",
    "home.install.ios.step3a": "Desplázate y toca",
    "home.install.ios.add_to_home": "Agregar a la Pantalla de Inicio",
    "home.install.ios.step3b": ", luego Agregar.",
    "home.install.ios.got_it": "Entendido",
    "home.social.h2": "Sigue nuestro trabajo",
    "home.social.sub": "Educación, consejos y contenido bilingüe para la comunidad.",
    "footer.about": "Contabilidad, preparación de impuestos, notaría, ITIN, reportes de impuesto de ventas y traducciones al español — todo en una plataforma de confianza.",
    "footer.call_to_schedule": "Llama para agendar",
    "footer.bilingual_support": "Atención bilingüe — Inglés y Español",
    "footer.navigate": "Navegar",
    "footer.qr_welcome": "Bienvenida QR",
    "services.eyebrow": "Servicios",
    "services.h1_a": "Soluciones. Estrategia.",
    "services.h1_b": "Estructura. Éxito.",
    "services.intro": "Apoyo bilingüe y práctico en los siete servicios que tu negocio y familia realmente necesitan.",
    "services.docs_heading": "Documentos que podrías necesitar",
    "services.start_intake": "Iniciar Admisión",
    "services.schedule": "Agendar Cita",
    "services.ask_question": "Hacer una pregunta",
    "services.label": "Servicio",
    "contact.eyebrow": "Contacto",
    "contact.h1_a": "Contáctanos.",
    "contact.h1_b": "Te responderemos pronto.",
    "contact.intro": "Correo, teléfono o envía un mensaje. Normalmente respondemos en 1 día hábil.",
    "contact.form.name": "Tu nombre",
    "contact.form.email": "Tu correo",
    "contact.form.subject": "Asunto (opcional)",
    "contact.form.message": "¿Cómo podemos ayudarte?",
    "contact.form.send": "Enviar Mensaje",
    "contact.success": "Gracias — nos pondremos en contacto pronto.",
    "contact.detail.email": "Correo",
    "contact.detail.phone": "Teléfono",
    "contact.detail.languages": "Idiomas",
    "contact.detail.languages_val": "Inglés y Español",
    "welcome.eyebrow": "Bienvenido",
    "welcome.h1": "Gracias por escanear.",
    "welcome.sub": "Elige lo que necesitas. Cada opción te lleva a una experiencia rápida y móvil.",
    "welcome.intake": "Iniciar Admisión",
    "welcome.intake_sub": "Cuéntanos sobre tu negocio o necesidad.",
    "welcome.book": "Agendar Cita",
    "welcome.book_sub": "Elige un horario que te convenga.",
    "welcome.portal": "Portal del Cliente",
    "welcome.portal_sub": "Sube documentos, ve reportes, paga facturas.",
    "welcome.refer": "Refiere un Amigo",
    "welcome.refer_sub": "Envíanos a alguien que nos necesite — ¡gracias!",
    "welcome.h2_a": "Bienvenido a Roman Numerals III.",
    "welcome.h2_b": "¿Cómo podemos ayudarte hoy?",
    "welcome.opt.bookkeeping": "Necesito ayuda con contabilidad",
    "welcome.opt.tax_prep": "Necesito preparación de impuestos",
    "welcome.opt.notary": "Necesito un notario",
    "welcome.opt.itin": "Necesito ayuda con ITIN",
    "welcome.opt.sales_tax": "Necesito reportes de impuesto de ventas",
    "welcome.opt.translation": "Necesito traducción al español",
    "welcome.opt.consultation": "Quiero agendar una consulta",
    "welcome.back_home": "Volver al inicio",
    "resources.eyebrow": "Recursos",
    "resources.h1": "Herramientas, guías y ayuda bilingüe — gratis.",
    "resources.intro": "Contenido curado de nuestra práctica y del IRS, diseñado para ayudarte a entender los números detrás de tu negocio.",
    "appts.eyebrow": "Citas",
    "appts.h1_a": "Agenda un horario.",
    "appts.h1_b": "Estaremos listos.",
    "appts.intro": "Elige el servicio y un horario que te convenga. Confirmaremos por correo en pocas horas.",
    "appts.form.service": "Servicio",
    "appts.form.date": "Fecha preferida",
    "appts.form.time": "Hora preferida",
    "appts.form.notes": "Notas (opcional)",
    "appts.form.submit": "Solicitar Cita",
    "appts.success": "Solicitud recibida — confirmaremos por correo.",
    "about.eyebrow": "Nosotros",
    "about.h1_a": "Una firma familiar",
    "about.h1_b": "construida con números y confianza.",
    "intake.eyebrow": "Admisión",
    "intake.h1": "Cuéntanos sobre tu necesidad",
    "intake.intro": "Comparte algunos detalles para prepararnos antes de nuestra primera conversación. Toma unos 3 minutos.",
    "intake.consent": "Acepto ser contactado sobre mi solicitud.",
    "intake.submit": "Enviar Admisión",
    "intake.success": "Gracias — nos pondremos en contacto.",
    "portal.eyebrow": "Portal Administrativo",
    "portal.invite_only.heading": "Acceso solo por invitación",
    "portal.invite_only.body": "Adelis registra a los nuevos clientes directamente. Completa tu intake o agenda una consulta, y recibirás un enlace personalizado por correo electrónico.",
    "portal.invite_only.cta_intake": "Iniciar intake",
    "portal.invite_only.cta_book": "Agendar llamada",
    "portal.welcome": "Bienvenido",
    "portal.upload_docs": "Subir Documentos",
    "portal.your_appts": "Tus Citas",
    "portal.your_docs": "Tus Documentos",
    "portal.reports": "Ver Reportes",
    "portal.no_data": "Aún no hay nada aquí.",
    "service.bookkeeping.short": "Libros organizados, cada mes.",
    "service.bookkeeping.desc": "Conciliación mensual, categorización y registros financieros limpios que te ayudan a tomar decisiones con confianza.",
    "service.bookkeeping.docs": "Estados de cuenta bancarios (PDF/CSV)|Recibos|Libros del año anterior (si los hay)|Catálogo de cuentas",
    "service.tax_prep.short": "Personas, familias y pequeños negocios.",
    "service.tax_prep.desc": "Preparación de impuestos precisa y a tiempo, con guía de deducciones para declaraciones personales y de negocio.",
    "service.tax_prep.docs": "Formularios W-2 / 1099|Declaración del año anterior|Identificación y SSN/ITIN|Recibos y registros de deducciones",
    "service.sales_tax.short": "Bien declarado. A tiempo.",
    "service.sales_tax.desc": "Declaraciones de impuesto de ventas mensuales o trimestrales adaptadas a tu jurisdicción y estructura de negocio.",
    "service.sales_tax.docs": "Reportes de ventas|Exportación del POS|Permiso de impuesto de ventas|Documentación de ventas exentas",
    "service.notary.short": "Notaría presencial y móvil.",
    "service.notary.desc": "Reconocimientos, juramentos, declaraciones juradas y documentos personales — rápido y profesional.",
    "service.notary.docs": "Identificación oficial vigente|Documento a notarizar (sin firmar)",
    "service.loan_signing.short": "Firma de documentos de préstamo certificada.",
    "service.loan_signing.desc": "Firmas profesionales de documentos de préstamo para refinanciamientos, compras, HELOC e hipotecas inversas.",
    "service.loan_signing.docs": "Paquete de documentos del préstamo|Identificación vigente para todos los firmantes",
    "service.itin.short": "Solicita o renueva tu ITIN.",
    "service.itin.desc": "Apoyo de Agente de Aceptación Certificado para que no tengas que enviar documentos originales por correo al IRS.",
    "service.itin.docs": "Pasaporte o documentos de identificación|Formulario W-7 (te ayudamos a completarlo)|Declaración de impuestos (si aplica)",
    "service.translation.short": "Documentos bilingües, bien hechos.",
    "service.translation.desc": "Traducción español-inglés de documentos personales, legales y de negocio con certificado de exactitud.",
    "service.translation.docs": "Documento original (cualquier formato)",
    "appts.consultation": "Consulta General",
    "appts.notes": "Notas",
    "appts.choose_date": "Elige una fecha",
    "appts.available_times": "Horarios disponibles",
    "appts.no_slots": "No hay horarios disponibles para esta fecha.",
    "appts.calendar_soon": "Sincronización con calendario (Google Calendar) próximamente.",
    "appts.lang_label": "Idioma preferido",
    "appts.full_name": "Nombre Completo",
    "appts.phone": "Teléfono",
    "appts.email": "Correo Electrónico",
    "appts.notes_optional": "Notas (opcional)",
    "appts.select_time": "Por favor selecciona un horario",
    "appts.requested": "Cita Solicitada",
    "appts.requested_body_a": "Recibimos tu solicitud para",
    "appts.requested_body_b": "el",
    "appts.requested_body_c": "a las",
    "appts.requested_body_d": "Confirmaremos pronto por correo.",
    "appts.book_another": "Agendar otra",
    "appts.toast_ok": "¡Cita solicitada! Recibirás una confirmación pronto.",
    "appts.toast_fail": "No se pudo enviar",
    // Intake page
    "intake.service.general": "Admisión General de Cliente",
    "intake.full_name": "Nombre Completo",
    "intake.phone": "Teléfono",
    "intake.email": "Correo Electrónico",
    "intake.lang_label": "Idioma preferido",
    "intake.address": "Dirección",
    "intake.urgency": "Urgencia",
    "intake.urgency.low": "Baja — sin prisa",
    "intake.urgency.normal": "Normal",
    "intake.urgency.high": "Alta — por favor prioriza",
    "intake.description": "Descripción de tu necesidad",
    "intake.attach_docs": "Adjuntar documentos",
    "intake.files_help": "Hasta 5 archivos, 10 MB cada uno. PDF, imágenes, CSV, TXT, Word, Excel, PowerPoint. Se bloquean ejecutables, scripts, torrents y archivos comprimidos.",
    "intake.files_max": "Máximo 5 archivos por admisión.",
    "intake.file_blocked_type": "Tipo de archivo bloqueado",
    "intake.file_too_large": "El archivo supera 10 MB",
    "intake.file_ready": "archivo listo para subir",
    "intake.files_ready": "archivos listos para subir",
    "intake.upload_failed": "Error al subir",
    "intake.login_tip_a": "Consejo:",
    "intake.login_tip_b": "inicia sesión",
    "intake.login_tip_c": "para adjuntar archivos a tu admisión.",
    "intake.consent_full": "Acepto que Roman Numerals III me contacte sobre mi solicitud y procese la información que proporcioné.",
    "intake.submitting": "Enviando…",
    "intake.confirm.thanks": "Gracias",
    "intake.confirm.body_a": "Tu admisión para",
    "intake.confirm.body_b": "ha sido recibida. Adelis la revisará y se pondrá en contacto pronto.",
    "intake.confirm.scan_title": "Archivos protegidos",
    "intake.confirm.scan_file_singular": "archivo fue",
    "intake.confirm.scan_file_plural": "archivos fueron",
    "intake.confirm.scan_body": "escaneados con ClamAV antes de llegar a Adelis. Cualquier archivo malicioso se bloquea y elimina automáticamente.",
    "intake.confirm.schedule": "Agendar Cita",
    "intake.confirm.open_portal": "Abrir Portal del Cliente",
    "intake.toast_consent": "Por favor confirma el consentimiento",
    "intake.toast_ok": "¡Admisión enviada!",
    "intake.toast_fail": "No se pudo enviar",
    "intake.extra.business_name": "Nombre del negocio",
    "intake.extra.months_behind": "¿Cuántos meses atrasados?",
    "intake.extra.filing_status": "Estado civil (soltero/casado/etc.)",
    "intake.extra.dependents": "Número de dependientes",
    "intake.extra.state": "Estado(s) donde cobras impuesto de ventas",
    "intake.extra.frequency": "Frecuencia (mensual/trimestral/anual)",
    "intake.extra.doc_type": "Tipo de documento a notarizar",
    "intake.extra.location": "Preferencia de ubicación (presencial/móvil)",
    "intake.extra.lender": "Prestamista / Compañía de título",
    "intake.extra.signing_date": "Fecha preferida de firma",
    "intake.extra.applicant_type": "¿Nuevo ITIN o Renovación?",
    "intake.extra.country": "País de ciudadanía",
    "intake.extra.source_lang": "Idioma de origen",
    "intake.extra.target_lang": "Idioma de destino",
    // Portal dashboard
    "portal.dashboard.title": "Tu panel",
    "portal.dashboard.intakes": "Mis Admisiones",
    "portal.dashboard.no_intakes": "Aún no hay admisiones.",
    "portal.dashboard.appts": "Citas",
    "portal.dashboard.no_appts": "Aún no hay citas.",
    "portal.dashboard.documents": "Documentos",
    "portal.dashboard.upload": "Subir",
    "portal.upload.file_label": "Archivo(s)",
    "portal.upload.selected": "seleccionado(s)",
    "portal.upload.account_label": "Cuenta",
    "portal.upload.account_placeholder": "ej. Cuenta Chase",
    "portal.upload.period_label": "Período",
    "portal.upload.period_none": "— cualquiera —",
    "portal.upload.type_label": "Tipo",
    "portal.upload.type.receipt": "Recibo",
    "portal.upload.type.bank_statement": "Estado",
    "portal.upload.type.invoice": "Factura",
    "portal.upload.type.check": "Cheque",
    "portal.upload.type.other": "Otro",
    "portal.upload.vendor_label": "Proveedor",
    "portal.upload.vendor_placeholder": "ej. Home Depot",
    "portal.upload.amount_label": "Monto ($)",
    "portal.upload.category_label": "Categoría",
    "portal.upload.category_other_placeholder": "Describe la categoría…",
    "portal.upload.notes_label": "Notas (opcional)",
    "portal.upload.notes_placeholder": "Cualquier detalle útil para la reconciliación",
    "portal.upload.cat.office_supplies": "Artículos de Oficina",
    "portal.upload.cat.meals_entertainment": "Comidas y Entretenimiento",
    "portal.upload.cat.fuel_auto": "Combustible y Auto",
    "portal.upload.cat.travel_lodging": "Viaje y Hospedaje",
    "portal.upload.cat.utilities": "Servicios Públicos",
    "portal.upload.cat.software_subscriptions": "Software y Suscripciones",
    "portal.upload.cat.rent_lease": "Renta y Arrendamiento",
    "portal.upload.cat.insurance": "Seguro",
    "portal.upload.cat.marketing_advertising": "Marketing y Publicidad",
    "portal.upload.cat.professional_services": "Servicios Profesionales",
    "portal.upload.cat.bank_card_fees": "Cargos Bancarios",
    "portal.upload.cat.cogs": "Costo de Bienes Vendidos (COGS)",
    "portal.upload.cat.other": "Otro",
    "portal.upload.subcategory_label": "Subcategoría (opcional)",
    "portal.upload.subcategory_placeholder": "ej. Gasolina, Cambio de aceite, Comida con cliente…",
    "portal.dashboard.messages": "Mensajes con Roman Numerals III",
    "portal.dashboard.write_message": "Escribe un mensaje…",
    "portal.dashboard.send": "Enviar",
    "portal.dashboard.no_messages": "Aún no hay mensajes.",
    "portal.dashboard.you": "Tú",
    "portal.dashboard.uploaded": "Subido",
    "portal.dashboard.upload_failed": "Error al subir",
    "portal.dashboard.msg_sent": "Mensaje enviado",
    "portal.dashboard.demo_hint": "Demo:",
    "portal.dashboard.loading": "Cargando…",
    "portal.dashboard.auth_failed": "Autenticación fallida",
    "portal.dashboard.account_created": "Cuenta creada",
    "portal.security_strip.heading": "Tu cuenta está protegida",
    "portal.security_strip.bcrypt": "Bcrypt Cifrado",
    "portal.security_strip.two_fa": "2FA Listo",
    "portal.security_strip.clamav": "Escaneado ClamAV",
    "portal.security_strip.encrypted_transit": "Cifrado en Tránsito",
    "portal.security_strip.audit_log": "Registro de Auditoría",
    "portal.security_strip.tagline": "Cada inicio de sesión se registra, cada archivo se escanea contra virus y cada contraseña se cifra con bcrypt. Soporte bilingüe · English & Español.",
    "portal.books.eyebrow": "Mis Libros",
    "portal.books.headline_one": "Abrir reportes financieros",
    "portal.books.headline_many": "libros · Abrir reportes financieros",
    "portal.books.sub": "Estado de Resultados, Balance General, Flujo de Efectivo, Antigüedad de CxC — acceso de solo lectura a tus libros.",
    // 2FA challenge screen
    "twofa.eyebrow": "Verificación en Dos Pasos",
    "twofa.confirm_title": "Confirma que eres tú",
    "twofa.sent_to_a": "Enviamos un código de 6 dígitos a",
    "twofa.sent_to_via_sms": "por SMS.",
    "twofa.sent_to_via_email": "por correo electrónico.",
    "twofa.code_label": "Código de Verificación",
    "twofa.recovery_label": "Código de Recuperación",
    "twofa.recovery_placeholder": "Uno de tus códigos de recuperación guardados",
    "twofa.verify_btn": "Verificar e Iniciar Sesión",
    "twofa.use_recovery_btn": "Usar Código de Recuperación",
    "twofa.resend": "Reenviar código",
    "twofa.use_recovery_link": "Usar un código de recuperación",
    "twofa.back_to_code": "Volver al código",
    "twofa.cancel": "Cancelar",
    "twofa.toast_sent": "Enviamos un código a",
    "twofa.toast_new_sent": "Nuevo código enviado a",
    "twofa.toast_resend_failed": "El reenvío falló",
    "twofa.toast_invalid": "Código inválido",
    // 2FA settings panel
    "twofa.settings.eyebrow": "Autenticación de Dos Factores",
    "twofa.settings.enabled": "Habilitada",
    "twofa.settings.not_enabled": "No habilitada",
    "twofa.settings.channel_label": "Canal:",
    "twofa.settings.recovery_remaining": "Códigos de recuperación restantes:",
    "twofa.settings.required_warning": "La 2FA es obligatoria para cuentas de administrador.",
    "twofa.settings.intro": "Agrega un paso adicional a tu inicio de sesión: después de ingresar tu contraseña, te enviaremos un código de 6 dígitos a tu correo electrónico o teléfono. Puedes iniciar sesión desde cualquier lugar siempre que tengas acceso al canal que elijas.",
    "twofa.settings.choose_channel": "Elige tu canal",
    "twofa.settings.email": "Correo",
    "twofa.settings.sms": "SMS",
    "twofa.settings.sms_disabled_hint": "(agrega un teléfono primero)",
    "twofa.settings.send_setup_code": "Enviar código de configuración",
    "twofa.settings.sending": "Enviando…",
    "twofa.settings.loading": "Cargando estado de 2FA…",
    "twofa.settings.confirm_setup_eyebrow": "Confirmar Configuración de 2FA",
    "twofa.settings.confirm_setup_title": "Ingresa el código que enviamos",
    "twofa.settings.confirm_sent_to": "Enviamos un código de 6 dígitos a",
    "twofa.settings.confirm_btn": "Confirmar y Habilitar 2FA",
    "twofa.settings.disable_link": "Deshabilitar 2FA",
    "twofa.settings.disable_hint": "Confirma tu contraseña para deshabilitar la 2FA.",
    "twofa.settings.your_password": "Tu contraseña",
    "twofa.settings.disable_btn": "Deshabilitar 2FA",
    "twofa.settings.toast_enabled": "2FA habilitada exitosamente",
    "twofa.settings.toast_disabled": "2FA deshabilitada",
    "twofa.settings.toast_setup_failed": "La configuración falló",
    "twofa.settings.toast_disable_failed": "No se pudo deshabilitar",
    "twofa.settings.toast_code_sent": "Código enviado a",
    // Recovery codes display
    "twofa.recovery.eyebrow": "Códigos de Recuperación",
    "twofa.recovery.title": "Guarda estos en un lugar seguro",
    "twofa.recovery.body_a": "Estos códigos de un solo uso te permitirán iniciar sesión si pierdes acceso a tu canal de verificación.",
    "twofa.recovery.body_b": "No los volverás a ver.",
    "twofa.recovery.body_c": "Guárdalos ahora.",
    "twofa.recovery.copy_all": "Copiar todos los códigos",
    "twofa.recovery.done": "Ya los guardé",
    "twofa.recovery.toast_copied": "Códigos de recuperación copiados al portapapeles",
    // Account security wrapper
    "portal.security.heading": "Seguridad de la Cuenta",
    // Client activity feed
    "portal.activity.eyebrow": "Actividad Reciente (últimos 30 días)",
    "portal.activity.title": "Tu historial de inicios de sesión",
    "portal.activity.loading": "Cargando…",
    "portal.activity.empty": "Aún no hay actividad registrada.",
    "portal.activity.ip_label": "IP:",
    // About page
    "about.meet": "Conoce a Adelis",
    "about.intro_1": "Roman Numerals III fue creado para ayudar a personas, familias, emprendedores y dueños de pequeños negocios a comprender mejor sus números, organizar sus registros y tomar decisiones financieras más inteligentes.",
    "about.intro_2_a": "Dirigida por",
    "about.intro_2_b": ", Roman Numerals III brinda apoyo práctico en contabilidad, preparación de impuestos, reportes de impuesto de ventas, servicios notariales, agente de firmas, apoyo de aceptación de ITIN y traducciones al español.",
    "about.journey.eyebrow": "El Camino",
    "about.journey.h2_a": "Cómo Roman Numerals III",
    "about.journey.h2_b": "llegó a ser.",
    "about.journey.sub": "Haz clic abajo para leer la historia de Adelis — las personas, las lecciones y el momento que convirtió un llamado en una firma.",
    "about.journey.title": "Adelis Roman — Fundadora de Roman Numerals III",
    "about.journey.founder": "Fundadora · Roman Numerals III",
    "about.journey.quote_1": "Siempre he creído que los números cuentan una historia. Mi trabajo es ayudar a las personas a entender la que sus números están contando — y escribir un mejor próximo capítulo.",
    "about.journey.p1": "Adelis Roman es la Fundadora de Roman Numerals III, una firma de servicios financieros de primer nivel construida sobre los pilares fundamentales de la educación, la confianza profunda y la alianza estratégica. Con una trayectoria profesional diversa que abarca banca en Wells Fargo, contabilidad corporativa y administración financiera especializada, Adelis empodera a individuos, familias y dueños de negocios para navegar panoramas financieros complejos con absoluta confianza.",
    "about.journey.p2": "El fundamento de Roman Numerals III está profundamente conectado con su historia personal y sus valores esenciales. Nacida en Puerto Rico, criada en Nueva York y establecida en Florida desde 2013, Adelis eligió un nombre de empresa que honra su herencia familiar como la tercera de siete hermanos. Este profundo compromiso con el legado impulsa su misión profesional: construir relaciones duraderas y multigeneracionales, y ofrecer soluciones impactantes centradas en la comunidad.",
    "about.journey.p3": "Hoy, su completo conjunto de servicios incluye preparación experta de impuestos, contabilidad de servicio completo, servicios de Agente de Aceptación del IRS, servicios de Agente Notarial de Firmas Certificado y traducción profesional de documentos. Demostrando un compromiso inquebrantable con la excelencia de la industria, Adelis está actualmente cursando su credencial de Agente Registrado (EA) — la designación fiscal más alta reconocida a nivel federal, otorgada por el Servicio de Rentas Internas (IRS). Mantiene un riguroso calendario de educación continua, asegurando que sus clientes se beneficien de perspectivas proactivas sobre cambios en las leyes tributarias, regulaciones del IRS y prácticas contables modernas.",
    "about.journey.p3b": "Como apasionada defensora del empoderamiento económico, Adelis dedica su experiencia a cerrar brechas de educación financiera. Está particularmente comprometida con ayudar a la creciente comunidad latina a dominar el sistema financiero, resolver asuntos fiscales complejos y construir riqueza sostenible.",
    "about.journey.quote_2": "Las personas a las que sirvo no son transacciones. Son madres, padres, dueños de negocios, soñadores y constructores. Merecen a alguien en su esquina que entienda tanto la hoja de cálculo como la historia detrás de ella.",
    "about.journey.p4_a": "Roman Numerals III es el resultado de ese llamado: una firma moderna, bilingüe y asistida por IA, construida para que el trabajo financiero y administrativo se sienta menos pesado y más esperanzador. El nombre mismo es una promesa silenciosa — la fuerza de una larga tradición (los números romanos) acompañada por los tres pilares que guían cada interacción:",
    "about.journey.p4_b": "Soluciones. Estrategia. Estructura.",
    "about.journey.p4_c": "Todo al servicio del cuarto pilar — tu",
    "about.journey.p4_d": "Éxito.",
    "about.journey.p5": "Con sede en Jacksonville, Florida, Adelis equilibra su liderazgo ejecutivo con sus roles como orgullosa madre de dos hijos adultos y abuela. En Roman Numerals III, su mandato permanece absoluto: educar, empoderar y servir con integridad inquebrantable, brindando verdadera tranquilidad financiera a cada cliente al que sirve.",
    "about.journey.cta_schedule": "Agenda una conversación con Adelis",
    "about.journey.cta_services": "Ve cómo podemos ayudarte",
    "about.cards.mission_title": "Nuestra Misión",
    "about.cards.mission_body": "Simplificar procesos financieros y administrativos complicados mientras ayudamos a los clientes a sentirse informados, preparados y confiados.",
    "about.cards.story_title": "Nuestra Historia",
    "about.cards.story_body": "Construida sobre años de experiencia práctica apoyando a familias y pequeños negocios en los momentos que más importan.",
    "about.cards.why_title": "Por qué Roman Numerals III",
    "about.cards.why_body": "Servicio profesional, apoyo bilingüe, tecnología moderna y herramientas con IA — una mejor experiencia de cliente desde el primer contacto hasta la entrega final.",
    "about.bilingual.eyebrow": "Apoyo Bilingüe",
    "about.bilingual.h2_a": "Inglés y",
    "about.bilingual.h2_b": "Español",
    "about.bilingual.body": "Desde la admisión hasta la presentación, cada interacción está disponible en el idioma en el que te sientes más cómodo. La confianza nace de la claridad.",
    "about.platform.title": "Una plataforma moderna y segura",
    "about.platform.body": "Contabilidad asistida por IA, subida segura de documentos y un portal del cliente que mantiene tus registros organizados y accesibles.",
    "about.platform.link": "Explora nuestros servicios",
    "about.follow.title": "Sigue a Adelis & Roman Numerals III",
    "about.follow.sub": "Consejos, educación y contenido bilingüe para la comunidad en redes sociales.",
    // Resources page
    "resources.topics_h2": "Temas",
    "resources.media_h2": "Medios · Próximamente",
    "resources.placeholder": "Próximamente",
    "resources.topic.bookkeeping.title": "Consejos de Contabilidad",
    "resources.topic.bookkeeping.body": "Construye libros limpios desde el inicio: categorización, cierre mensual y cómo evitar los errores más comunes.",
    "resources.topic.tax.title": "Consejos de Impuestos",
    "resources.topic.tax.body": "Estrategias de todo el año para personas y pequeños negocios para conservar más de lo que ganas.",
    "resources.topic.sales_tax.title": "Consejos de Impuesto de Ventas",
    "resources.topic.sales_tax.body": "Frecuencias de presentación, nexo y cómo evitar multas — explicado de forma sencilla.",
    "resources.topic.itin.title": "Información de ITIN",
    "resources.topic.itin.body": "Quién lo necesita, qué documentos reunir y cómo un Agente de Aceptación Certificado lo hace más fácil.",
    "resources.topic.notary.title": "Preparación para Notaría",
    "resources.topic.notary.body": "Qué traer, quién debe estar presente y cómo evitar una notarización rechazada.",
    "resources.topic.spanish_biz.title": "Educación de Negocios en Español",
    "resources.topic.spanish_biz.body": "Recursos en español para emprendedores y dueños de pequeñas empresas.",
    "resources.topic.personal.title": "Educación de Finanzas Personales",
    "resources.topic.personal.body": "Presupuesto, ahorro y cómo usar tus números para planificar un mejor futuro.",
    "resources.topic.ai.title": "IA para Pequeños Negocios",
    "resources.topic.ai.body": "Formas prácticas de usar herramientas modernas — incluyendo IA — para administrar mejor tu negocio.",
    "resources.media.reels.label": "Reels",
    "resources.media.reels.body": "Consejos cortos y compartibles en redes sociales.",
    "resources.media.longform.label": "Videos Largos",
    "resources.media.longform.body": "Profundizaciones en YouTube.",
    "resources.media.podcast.label": "Episodios de Podcast",
    "resources.media.podcast.body": "Conversaciones con fundadores y profesionales.",
    "resources.media.community.label": "Capacitaciones Comunitarias",
    "resources.media.community.body": "Talleres en vivo y bajo demanda.",
    // ============ ADMIN (Spanish) ============
    "admin.eyebrow": "Administración · Roman Numerals III",
    "admin.welcome": "Bienvenido",
    "admin.back_to_admin": "Volver a Administración",
    "admin.nav.revenue": "Ingresos", "admin.nav.tasks": "Tareas", "admin.nav.sms": "SMS",
    "admin.nav.email": "Correo", "admin.nav.referrals": "Referencias", "admin.nav.leads": "Prospectos",
    "admin.nav.activity": "Actividad", "admin.nav.back_office": "Trastienda",
    "admin.stats.clients": "Clientes", "admin.stats.books": "Libros de Cliente",
    "admin.stats.new_leads": "Nuevos Prospectos", "admin.stats.intakes": "Admisiones",
    "admin.stats.upcoming_appts": "Citas Próximas", "admin.stats.open_tasks": "Tareas Abiertas",
    "admin.stats.overdue": "vencidas",
    "admin.col.date": "Fecha", "admin.col.time": "Hora", "admin.col.client": "Cliente",
    "admin.col.service": "Servicio", "admin.col.urgency": "Urgencia", "admin.col.status": "Estado",
    "admin.col.actions": "Acciones", "admin.col.name": "Nombre", "admin.col.email": "Correo",
    "admin.col.phone": "Teléfono", "admin.col.lang": "Idioma", "admin.col.joined": "Ingresó",
    "admin.intakes.heading": "Admisiones Recibidas", "admin.intakes.empty": "Aún no hay admisiones.",
    "admin.appts.heading": "Citas Próximas", "admin.appts.empty": "Aún no hay citas.",
    "admin.appts.approve": "Aprobar", "admin.appts.cancel": "Cancelar",
    "admin.appt_status.pending": "Pendiente", "admin.appt_status.approved": "Aprobada",
    "admin.appt_status.rescheduled": "Reprogramada", "admin.appt_status.cancelled": "Cancelada", "admin.appt_status.completed": "Completada",
    "admin.clients.heading": "Clientes",
    "admin.clients.hint": "Haz clic en cualquier fila para ver notas internas y estado",
    "admin.clients.empty": "Aún no hay clientes.",
    "admin.documents.heading": "Documentos Recientes", "admin.documents.download": "Descargar",
    "admin.documents.empty": "Aún no se han subido documentos.",
    "admin.documents.edit": "Editar",
    "admin.doc_edit.heading": "Modificar Documento",
    "admin.doc_edit.save": "Guardar Cambios",
    "admin.doc_edit.cancel": "Cancelar",
    "admin.doc_edit.none": "ninguna",
    "admin.doc_edit.saved": "Metadatos actualizados",
    "admin.doc_edit.failed": "Actualización fallida",
    "admin.messages.heading": "Mensajes de Clientes",
    "admin.messages.refresh": "Actualizar",
    "admin.messages.empty": "Aún no hay mensajes de clientes.",
    "admin.messages.reply_placeholder": "Escribe tu respuesta… el cliente recibirá un correo instantáneo.",
    "admin.messages.send_reply": "Enviar Respuesta",
    "admin.messages.reply_sent": "Respuesta enviada — cliente notificado por correo.",
    "admin.security.heading": "Seguridad de la Cuenta",
    "admin.scan.heading": "Estado del Antivirus",
    "admin.scan.refresh": "Actualizar",
    "admin.scan.engine": "Motor",
    "admin.scan.engine_ok": "En línea",
    "admin.scan.engine_offline": "Fuera de línea",
    "admin.scan.latency": "Latencia",
    "admin.scan.clean": "Archivos Limpios",
    "admin.scan.quarantined": "En Cuarentena",
    "admin.scan.blocked": "Bloqueados (30d)",
    "admin.scan.total": "Total de Archivos",
    "admin.scan.recent_heading": "Bloqueos recientes de malware",
    "admin.scan.recent_empty": "No se detectaron cargas maliciosas. ClamAV vigila cada archivo.",
    "admin.scan.col_when": "Cuándo",
    "admin.scan.col_signature": "Firma",
    "admin.scan.col_filename": "Nombre de archivo",
    "admin.scan.col_uploader": "Remitente",
    "admin.scan.col_ip": "IP",
    "admin.scan.anonymous": "anónimo",
    "admin.toast.status_updated": "Estado actualizado",
    "admin.toast.updated": "Actualizado",
    "admin.toast.failed": "Falló",
    // -- Tasks --
    "admin.tasks.heading": "Tareas", "admin.tasks.center_eyebrow": "Administración · Centro de Tareas",
    "admin.tasks.new_task": "Nueva Tarea", "admin.tasks.edit_task": "Editar Tarea",
    "admin.tasks.create_task": "Crear Tarea", "admin.tasks.save_changes": "Guardar Cambios",
    "admin.tasks.add": "Agregar", "admin.tasks.move_to": "Mover a",
    "admin.tasks.overdue_caps": "VENCIDA",
    "admin.tasks.cycle_priority_title": "Clic para cambiar prioridad",
    "admin.tasks.delete_title": "Eliminar tarea", "admin.tasks.confirm_delete": "¿Eliminar",
    "admin.tasks.empty_column": "Sin tareas aquí.",
    "admin.tasks.search_placeholder": "Título o descripción…",
    "admin.tasks.all_priorities": "Todas las Prioridades",
    "admin.tasks.overdue_only": "Solo vencidas",
    "admin.tasks.quick_add_placeholder": "Escribe el título de una tarea rápida y presiona Enter o haz clic en Agregar →",
    "admin.tasks.quick_add_disabled_title": "Escribe el título de la tarea primero",
    "admin.tasks.status.pending": "Pendiente", "admin.tasks.status.in_progress": "En Progreso", "admin.tasks.status.complete": "Completada",
    "admin.tasks.priority.low": "Baja", "admin.tasks.priority.normal": "Normal", "admin.tasks.priority.high": "Alta", "admin.tasks.priority.urgent": "Urgente",
    "admin.tasks.field.title": "Título", "admin.tasks.field.title_placeholder": "ej. Llamar al cliente sobre la revisión del Q4",
    "admin.tasks.field.description": "Descripción", "admin.tasks.field.priority": "Prioridad", "admin.tasks.field.due_date": "Fecha Límite",
    "admin.tasks.stat.total": "Total", "admin.tasks.stat.overdue": "Vencidas",
    "admin.tasks.toast.title_required": "El título es obligatorio",
    "admin.tasks.toast.created": "Tarea creada", "admin.tasks.toast.updated": "Tarea actualizada",
    "admin.tasks.toast.save_failed": "No se pudo guardar", "admin.tasks.toast.load_failed": "No se pudieron cargar las tareas",
    "admin.tasks.toast.update_failed": "No se pudo actualizar", "admin.tasks.toast.delete_failed": "No se pudo eliminar",
    "admin.tasks.toast.deleted": "Tarea eliminada", "admin.tasks.toast.added": "Tarea agregada",
    // -- Activity --
    "admin.activity.eyebrow": "Seguridad", "admin.activity.heading": "Registro de Actividad",
    "admin.activity.description": "Cada autenticación y operación sensible, con dirección IP + agente de usuario. Se purga automáticamente después de 180 días.",
    "admin.activity.refresh": "Actualizar",
    "admin.activity.stat.successful": "Inicios exitosos", "admin.activity.stat.failed": "Inicios fallidos",
    "admin.activity.stat.locked": "Actualmente bloqueados", "admin.activity.lockouts_active": "bloqueos por fuerza bruta activos",
    "admin.activity.last_days": "últimos {n} días", "admin.activity.top_action": "Acción principal",
    "admin.activity.events_count": "{n} eventos",
    "admin.activity.filter.action": "Acción", "admin.activity.filter.last": "Últimos",
    "admin.activity.filter.search": "Buscar por correo / IP",
    "admin.activity.search_placeholder": "usuario@ejemplo.com o 1.2.3.4",
    "admin.activity.range.24h": "24 horas", "admin.activity.range.7d": "7 días",
    "admin.activity.range.30d": "30 días", "admin.activity.range.90d": "90 días",
    "admin.activity.showing": "Mostrando {n} de {total} eventos",
    "admin.activity.empty": "Ningún evento coincide con el filtro actual.",
    "admin.activity.col.when": "Cuándo", "admin.activity.col.action": "Acción", "admin.activity.col.user": "Usuario",
    "admin.activity.col.ip": "Dirección IP", "admin.activity.col.user_agent": "Agente de Usuario", "admin.activity.col.details": "Detalles",
    "admin.activity.action.all": "Todas las acciones",
    "admin.activity.action.login_success": "Inicio exitoso",
    "admin.activity.action.login_failed": "Inicio fallido",
    "admin.activity.action.login_2fa_required": "Desafío 2FA enviado",
    "admin.activity.action.login_2fa_verified": "2FA verificada",
    "admin.activity.action.login_2fa_failed": "2FA falló",
    "admin.activity.action.logout": "Cerrar sesión",
    "admin.activity.action.register": "Registro",
    "admin.activity.action.2fa_setup_completed": "2FA activada",
    "admin.activity.action.2fa_disabled": "2FA desactivada",
    "admin.activity.action.2fa_recovery_used": "Código de recuperación usado",
    // -- Revenue --
    "admin.revenue.eyebrow": "Administración · Ingresos", "admin.revenue.heading": "Panel de Ingresos",
    "admin.revenue.as_of": "Al", "admin.revenue.vs_last_month": "vs. el mes pasado",
    "admin.revenue.kpi.total_clients": "Total de Clientes", "admin.revenue.kpi.monthly": "Ingresos Mensuales",
    "admin.revenue.kpi.pending": "Ingresos Pendientes", "admin.revenue.kpi.pending_sub": "Saldos de facturas sin pagar",
    "admin.revenue.kpi.collected_mtd": "Cobrado MTD", "admin.revenue.kpi.ytd": "YTD",
    "admin.revenue.kpi.upcoming_appts": "Citas Próximas", "admin.revenue.kpi.total_lower": "total",
    "admin.revenue.kpi.lead_conversion": "Conversión de Prospectos", "admin.revenue.leads_lower": "prospectos",
    "admin.revenue.kpi.alltime": "Ingresos Totales",
    "admin.revenue.trend.eyebrow": "Tendencia de 12 Meses", "admin.revenue.trend.heading": "Ingresos en el Tiempo",
    "admin.revenue.legend.billed": "Facturado", "admin.revenue.legend.collected": "Cobrado",
    "admin.revenue.legend.total": "Prospectos", "admin.revenue.legend.converted": "Convertidos",
    "admin.revenue.by_service.eyebrow": "Por Servicio", "admin.revenue.by_service.heading": "Ingresos por Servicio", "admin.revenue.by_service.empty": "Aún no hay facturas.",
    "admin.revenue.by_source.eyebrow": "Por Origen", "admin.revenue.by_source.heading": "Conversión de Prospectos", "admin.revenue.by_source.empty": "Aún no hay prospectos.",
    "admin.revenue.billed_lower": "facturado", "admin.revenue.collected_lower": "cobrado", "admin.revenue.invoices_lower": "facturas",
    "admin.revenue.drill_in": "Profundizar", "admin.revenue.drill.backoffice": "Trastienda (facturas y libros)",
    "admin.revenue.toast.load_failed": "No se pudieron cargar los ingresos",
    // -- Referrals --
    "admin.referrals.eyebrow": "Administración · Referencias", "admin.referrals.heading": "Sistema de Referencias",
    "admin.referrals.new": "Nueva Referencia", "admin.referrals.log": "Registrar Referencia",
    "admin.referrals.top_referrers": "Mejores Referidores", "admin.referrals.search_placeholder": "Nombre o correo…",
    "admin.referrals.all_statuses": "Todos", "admin.referrals.empty": "Aún no hay referencias. Haz clic en Nueva Referencia para registrar la primera.",
    "admin.referrals.unknown": "Desconocido", "admin.referrals.issue": "Emitir", "admin.referrals.mark_paid": "Pagada",
    "admin.referrals.confirm_delete": "¿Eliminar esta referencia?",
    "admin.referrals.referrals_lower": "referencias", "admin.referrals.converted_lower": "convertidas",
    "admin.referrals.col.referrer_referred": "Referidor → Referido",
    "admin.referrals.col.source": "Origen", "admin.referrals.col.reward": "Recompensa", "admin.referrals.col.created": "Creada",
    "admin.referrals.stat.total": "Total de Referencias", "admin.referrals.stat.rewards_pending": "Recompensas Pendientes", "admin.referrals.stat.rewards_owed": "Recompensas Adeudadas",
    "admin.referrals.status.pending": "Pendiente", "admin.referrals.status.contacted": "Contactado",
    "admin.referrals.status.converted": "Convertida", "admin.referrals.status.rewarded": "Recompensada", "admin.referrals.status.declined": "Rechazada",
    "admin.referrals.source.word_of_mouth": "Boca a Boca", "admin.referrals.source.email": "Correo",
    "admin.referrals.source.text": "SMS / Texto", "admin.referrals.source.qr": "Código QR",
    "admin.referrals.source.social": "Redes Sociales", "admin.referrals.source.other": "Otro",
    "admin.referrals.reward_type.discount_15": "15% de Descuento", "admin.referrals.reward_type.discount_25": "25% de Descuento",
    "admin.referrals.reward_type.credit_50": "$50 de Crédito", "admin.referrals.reward_type.credit_100": "$100 de Crédito",
    "admin.referrals.reward_type.free_notary": "Notaría Gratis", "admin.referrals.reward_type.free_consult": "Consulta Gratis",
    "admin.referrals.reward_type.custom": "Personalizada",
    "admin.referrals.reward_status.pending": "Recompensa Pendiente", "admin.referrals.reward_status.issued": "Recompensa Emitida", "admin.referrals.reward_status.paid_out": "Pagada",
    "admin.referrals.form.referrer_client": "Referidor (cliente existente)", "admin.referrals.form.referrer_none": "— No es cliente (ingresa nombre/correo abajo) —",
    "admin.referrals.form.referrer_name": "Nombre del referidor", "admin.referrals.form.referrer_email": "Correo del referidor",
    "admin.referrals.form.referred_party": "Persona Referida", "admin.referrals.form.referred_name": "Nombre completo *",
    "admin.referrals.form.reward": "Recompensa (opcional)", "admin.referrals.form.no_reward": "— Sin recompensa —",
    "admin.referrals.form.reward_amount": "Valor en dólares (opcional)",
    "admin.referrals.form.reward_note": "Nota de recompensa (ej. aplicar a la próxima factura)",
    "admin.referrals.form.notes": "Notas",
    "admin.referrals.toast.referred_required": "Nombre de la persona referida obligatorio", "admin.referrals.toast.logged": "Referencia registrada",
    "admin.referrals.toast.load_failed": "No se pudieron cargar las referencias",
    "admin.referrals.toast.reward_issued": "Recompensa marcada como emitida",
    "admin.referrals.toast.paid_out": "Marcada como pagada", "admin.referrals.toast.deleted": "Eliminada",
    // -- SMS --
    "admin.sms.eyebrow": "Administración · Flujos de SMS", "admin.sms.heading": "Automatización de SMS",
    "admin.sms.live": "En Vivo", "admin.sms.disabled": "Desactivado",
    "admin.sms.twilio_live": "Twilio Activo", "admin.sms.twilio_mocked": "Simulado · Agrega credenciales de Twilio para activarlo",
    "admin.sms.twilio_not_configured": "Twilio no está configurado — la prueba renderizará el mensaje pero no lo enviará (registrado en la consola del backend).",
    "admin.sms.stat.total": "Total de Flujos", "admin.sms.stat.enabled": "Activos", "admin.sms.stat.disabled": "Desactivados", "admin.sms.stat.runs": "Pruebas Ejecutadas",
    "admin.sms.placeholders": "Variables", "admin.sms.click_chip": "Haz clic en un chip para insertarlo en la plantilla en {lang} a continuación.",
    "admin.sms.template": "Plantilla", "admin.sms.chars": "caracteres", "admin.sms.segments": "segmentos SMS", "admin.sms.segment": "segmento",
    "admin.sms.preview": "Vista previa", "admin.sms.preview_en": "Vista EN", "admin.sms.preview_es": "Vista ES",
    "admin.sms.send_test": "Enviar Prueba", "admin.sms.send": "Enviar",
    "admin.sms.toggle_enable": "Activar flujo", "admin.sms.toggle_disable": "Desactivar flujo",
    "admin.sms.toast.saved": "Flujo guardado", "admin.sms.toast.enabled": "Flujo activado", "admin.sms.toast.disabled": "Flujo desactivado",
    "admin.sms.toast.load_failed": "No se pudieron cargar los flujos", "admin.sms.toast.preview_failed": "Falló la vista previa",
    "admin.sms.toast.enter_phone": "Ingresa un número de teléfono", "admin.sms.toast.send_failed": "Falló el envío",
    "admin.sms.toast.test_mocked": "SMS de prueba renderizado (SIMULADO — Twilio no configurado). Cuerpo impreso en el registro del backend.",
    "admin.sms.toast.test_sent": "SMS de prueba enviado a",
    // -- Email --
    "admin.email.eyebrow": "Administración · Campañas de Correo", "admin.email.heading": "Automatización de Correo",
    "admin.email.resend_live": "Resend Activo", "admin.email.resend_mocked": "Simulado · Agrega la clave de Resend para activarlo",
    "admin.email.resend_not_configured": "Resend no está configurado — la prueba renderizará el correo pero no lo entregará (registrado en el backend).",
    "admin.email.stat.campaigns": "Campañas", "admin.email.stat.templates": "Plantillas de Correo",
    "admin.email.step": "Paso", "admin.email.day": "Día",
    "admin.email.series": "Serie", "admin.email.steps": "pasos", "admin.email.broadcast": "Difusión",
    "admin.email.subject": "Asunto", "admin.email.body_html": "Cuerpo · HTML",
    "admin.email.html_supported": "HTML sin formato soportado", "admin.email.close": "Cerrar",
    "admin.email.save_all": "Guardar Todos los Pasos", "admin.email.hide_editor": "Ocultar editor de correo",
    "admin.email.edit_emails_prefix": "Editar", "admin.email.emails_singular": "correo", "admin.email.emails_plural": "correos",
    "admin.email.toggle_enable": "Activar campaña", "admin.email.toggle_disable": "Desactivar campaña",
    "admin.email.toast.saved": "Campaña guardada", "admin.email.toast.enabled": "Campaña activada", "admin.email.toast.disabled": "Campaña desactivada",
    "admin.email.toast.load_failed": "No se pudieron cargar las campañas",
    "admin.email.toast.enter_email": "Ingresa un correo", "admin.email.toast.test_mocked": "Renderizado (SIMULADO — Resend no configurado). Asunto:",
    "admin.email.toast.test_sent_prefix": "Enviado a",
    // -- Client Drawer --
    "admin.client.drawer_eyebrow": "Cliente · Notas Internas",
    "admin.client.no_status": "Sin estado",
    "admin.client.status.active": "Activo", "admin.client.status.waiting_on_documents": "Esperando Documentos",
    "admin.client.status.tax_return_pending": "Declaración Pendiente", "admin.client.status.follow_up_scheduled": "Seguimiento Programado",
    "admin.client.status.in_progress": "En Progreso", "admin.client.status.on_hold": "En Espera",
    "admin.client.status.completed": "Completado", "admin.client.status.archived": "Archivado",
    "admin.client.set_internal_status": "Asignar Estado Interno", "admin.client.quick_jot": "Nota Rápida",
    "admin.client.add_detailed_note": "Agregar Nota Detallada", "admin.client.append_note": "Agregar Nota",
    "admin.client.timeline": "Cronología", "admin.client.entries": "entradas",
    "admin.client.timeline_empty": "Aún no hay notas. Usa los botones de Nota Rápida arriba para registrar con un clic.",
    "admin.client.note_placeholder": "Hablé con el cliente sobre la revisión del Q4; pedí el 1099-NEC 2024 para fin de semana.",
    "admin.client.tag_label_prefix": "etiqueta", "admin.client.origin": "Origen",
    "admin.client.referral_link": "Enlace de Referencia Personal",
    "admin.client.referral_hint": "Comparte este enlace por correo, texto o QR. Cualquier referencia regresará etiquetada a {name} automáticamente.",
    "admin.client.this_client": "este cliente", "admin.client.copy": "Copiar",
    "admin.client.send.heading": "Enviar al Cliente",
    "admin.client.send.hint": "Ambas acciones envían un correo a {name} al instante.",
    "admin.client.send.upload_heading": "Subir Documento Para el Cliente",
    "admin.client.send.upload_btn": "Compartir con Cliente",
    "admin.client.send.doc_sent": "Documento compartido — cliente notificado por correo.",
    "admin.client.send.doc_failed": "Subida fallida",
    "admin.client.send.too_large": "Archivo demasiado grande (máx 15MB)",
    "admin.client.send.msg_heading": "Enviar Mensaje",
    "admin.client.send.msg_placeholder": "Escribe un mensaje rápido — recibirá un correo instantáneo y lo verá en su portal.",
    "admin.client.send.msg_btn": "Enviar Mensaje",
    "admin.client.send.msg_sent": "Mensaje enviado — cliente notificado por correo.",
    "admin.client.send.msg_failed": "Envío fallido",
    "admin.client.send.cat.general": "General",
    "admin.client.send.cat.tax_return": "Declaración de Impuestos",
    "admin.client.send.cat.bookkeeping": "Contabilidad",
    "admin.client.send.cat.notary": "Notaría",
    "admin.client.send.cat.itin": "ITIN",
    "admin.client.send.cat.translation": "Traducción",
    "admin.client.send.cat.invoice": "Factura",
    "admin.client.send.cat.statement": "Estado de Cuenta",
    "admin.client.send.cat.other": "Otro",
    "admin.import.heading": "Importar CSV Bancario/Tarjeta",
    "admin.import.hint": "Sube un CSV de transacciones para reconciliar contra los recibos subidos.",
    "admin.import.account_label": "Etiqueta de la cuenta",
    "admin.import.account_placeholder": "ej. Cuenta Chase Business",
    "admin.import.file_label": "Archivo CSV",
    "admin.import.submit": "Importar CSV",
    "admin.import.success": "Se importaron {n} transacciones",
    "admin.import.failed": "Importación fallida",
    "admin.import.confirm_delete": "¿Eliminar esta importación y sus {n} transacciones?",
    "admin.import.deleted": "Lote eliminado",
    "admin.import.delete_failed": "Eliminación fallida",
    "admin.import.recent_batches": "Importaciones Recientes",
    "admin.import.txns": "trans",
    "admin.import.txns_lower": "transacciones importadas",
    "admin.import.debits": "Débitos",
    "admin.import.credits": "Créditos",
    "admin.import.show_txns": "Mostrar",
    "admin.import.hide_txns": "Ocultar",
    "admin.import.col.date": "Fecha",
    "admin.import.col.description": "Descripción",
    "admin.import.col.amount": "Monto",
    "admin.import.col.status": "Estado",
    "admin.import.cleared_count": "Reconciliadas",
    "admin.import.cleared_total": "Total reconciliado",
    "admin.import.suggest_matches": "Buscar recibo coincidente",
    "admin.import.suggested_matches": "Recibos Sugeridos",
    "admin.import.no_matches": "No se encontraron coincidencias confiables.",
    "admin.import.strong": "FUERTE",
    "admin.import.likely": "PROBABLE",
    "admin.import.weak": "DÉBIL",
    "admin.import.confirm": "Confirmar",
    "admin.import.unmatch": "Desvincular coincidencia",
    "admin.import.match_confirmed": "Coincidencia confirmada",
    "admin.import.match_failed": "Coincidencia fallida",
    "admin.import.toggle_failed": "Actualización fallida",
    "admin.import.auto_match_btn": "Auto-emparejar",
    "admin.import.auto_matched": "Se emparejaron {n} de {total} pendientes",
    "admin.import.auto_match_failed": "Auto-emparejamiento fallido",
    "admin.import.post_ready": "Listo para publicar en el libro",
    "admin.import.post_hint": "{n} transacciones reconciliadas se registrarán en tu P&L / Balance",
    "admin.import.post_btn": "Publicar en Libros",
    "admin.import.confirm_post": "¿Publicar {n} transacciones reconciliadas en el libro mayor?",
    "admin.import.posted": "Se publicaron {n} en el libro",
    "admin.import.post_failed": "Publicación fallida",
    "admin.import.no_cleared": "No hay transacciones reconciliadas para publicar",
    "admin.client.delete_note": "Eliminar nota", "admin.client.confirm_delete_note": "¿Eliminar esta nota?",
    "admin.client.by": "por", "admin.client.invoices_on_file": "facturas registradas",
    "admin.client.count.intakes": "Admisiones", "admin.client.count.appts": "Citas",
    "admin.client.count.docs": "Docs", "admin.client.count.books": "Libros",
    "admin.client.tag_label.called": "Llamé al cliente", "admin.client.tag_label.emailed": "Envié correo",
    "admin.client.tag_label.texted": "Envié SMS", "admin.client.tag_label.met_in_person": "Reunión en persona",
    "admin.client.tag_label.waiting_on_docs": "Esperando documentos", "admin.client.tag_label.documents_received": "Documentos recibidos",
    "admin.client.tag_label.tax_pending": "Declaración pendiente", "admin.client.tag_label.follow_up": "Seguimiento programado",
    "admin.client.tag_label.payment_received": "Pago recibido", "admin.client.tag_label.payment_pending": "Pago pendiente",
    "admin.client.tag_body.called": "Llamé al cliente", "admin.client.tag_body.emailed": "Envié correo al cliente",
    "admin.client.tag_body.texted": "Envié SMS al cliente", "admin.client.tag_body.met_in_person": "Reunión en persona",
    "admin.client.tag_body.waiting_on_docs": "Esperando documentos", "admin.client.tag_body.documents_received": "Documentos recibidos",
    "admin.client.tag_body.tax_pending": "Declaración pendiente", "admin.client.tag_body.follow_up": "Seguimiento programado",
    "admin.client.tag_body.payment_received": "Pago recibido", "admin.client.tag_body.payment_pending": "Pago pendiente",
    "admin.client.checklist.heading": "Listas de Documentos",
    "admin.client.checklist.empty": "Aún no hay listas. Elige una plantilla de servicio arriba para crear una con los documentos requeridos.",
    "admin.client.checklist_template.bookkeeping": "Contabilidad", "admin.client.checklist_template.tax_prep": "Preparación de Impuestos",
    "admin.client.checklist_template.sales_tax": "Impuesto de Ventas", "admin.client.checklist_template.notary": "Notaría",
    "admin.client.checklist_template.loan_signing": "Firma de Préstamo", "admin.client.checklist_template.itin": "ITIN",
    "admin.client.checklist_template.translation": "Traducción", "admin.client.checklist_template.custom": "Personalizada (vacía)",
    "admin.client.item_status.missing": "FALTA", "admin.client.item_status.pending": "PENDIENTE", "admin.client.item_status.received": "RECIBIDO",
    "admin.client.item_status_cycle_title": "Clic para cambiar estado",
    "admin.client.received_lower": "recibidos", "admin.client.missing_lower": "faltan",
    "admin.client.delete_checklist_title": "Eliminar lista",
    "admin.client.confirm_delete_checklist": "¿Eliminar la lista",
    "admin.client.remove_item": "Quitar elemento",
    "admin.client.add_custom_item": "Agregar elemento personalizado…",
    "admin.client.toast.checklist_load_failed": "No se pudieron cargar las listas",
    "admin.client.toast.checklist_added": "lista agregada", "admin.client.toast.checklist_add_failed": "No se pudo agregar la lista",
    "admin.client.toast.checklist_deleted": "Lista eliminada",
    "admin.client.toast.add_item_failed": "No se pudo agregar el elemento",
    "admin.client.toast.load_failed": "No se pudo cargar el cliente",
    "admin.client.toast.status_update_failed": "No se pudo actualizar el estado",
    "admin.client.toast.note_added": "Nota agregada", "admin.client.toast.note_add_failed": "No se pudo agregar la nota",
    "admin.client.toast.note_deleted": "Nota eliminada", "admin.client.toast.note_delete_failed": "No se pudo eliminar",
    "admin.client.toast.referral_copied": "Enlace de referencia copiado", "admin.client.toast.copy_failed": "No se pudo copiar",
    // -- Back Office --
    "admin.bo.eyebrow": "Trastienda", "admin.bo.heading": "Libros de Cliente",
    "admin.bo.subtitle": "Cada cliente tiene su propio juego de libros. Abre uno para empezar a trabajar.",
    "admin.bo.new_book": "Nuevo Libro", "admin.bo.new_client_book": "Nuevo Libro de Cliente",
    "admin.bo.search_placeholder": "Buscar por cliente o nombre del libro…",
    "admin.bo.empty": "Aún no hay libros. Crea uno para empezar.",
    "admin.bo.client_label": "Cliente", "admin.bo.currency_label": "Moneda",
    "admin.bo.accounts_lower": "cuentas", "admin.bo.txns_lower": "transacciones",
    "admin.bo.open": "Abrir", "admin.bo.all_books": "Todos los Libros",
    "admin.bo.add_account": "Agregar Cuenta", "admin.bo.search_account": "Buscar cuenta…",
    "admin.bo.filter_all": "Todas", "admin.bo.no_match": "Ninguna cuenta coincide con el filtro.",
    "admin.bo.closing_packet": "Cierre Mensual", "admin.bo.closing_packet_full": "Paquete de Cierre Mensual",
    "admin.bo.creating": "Creando…", "admin.bo.create_book": "Crear Libro",
    "admin.bo.field.book_name": "Nombre del Libro / Negocio", "admin.bo.field.book_name_placeholder": "ej. Acme Bakery LLC",
    "admin.bo.field.entity_type": "Tipo de Entidad", "admin.bo.field.currency": "Moneda",
    "admin.bo.field.fy_start": "Inicio del Año Fiscal (Mes)",
    "admin.bo.field.ein": "EIN (opcional)", "admin.bo.field.link_client": "Vincular a Cliente (opcional)",
    "admin.bo.field.not_linked": "— Sin vincular aún —",
    "admin.bo.field.type": "Tipo", "admin.bo.field.name_en": "Nombre (Inglés)", "admin.bo.field.name_es": "Nombre (Español)",
    "admin.bo.account_type.asset": "Activo", "admin.bo.account_type.liability": "Pasivo",
    "admin.bo.account_type.equity": "Capital", "admin.bo.account_type.income": "Ingresos",
    "admin.bo.account_type.cogs": "Costo de Bienes Vendidos", "admin.bo.account_type.expense": "Gastos",
    "admin.bo.col.code": "Código", "admin.bo.col.subtype": "Subtipo",
    "admin.bo.col.balance": "Saldo", "admin.bo.col.active": "Activa",
    "admin.bo.toast.book_created": "Libro creado con plan de cuentas completo",
    "admin.bo.toast.create_failed": "No se pudo crear",
    "admin.bo.toast.account_added": "Cuenta agregada",
  },
};

const STORAGE_KEY = "rn3.lang";
const SUPPORTED = ["en", "es"];

function detectInitialLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED.includes(saved)) return saved;
    const browser = (navigator.language || "en").toLowerCase().split("-")[0];
    return SUPPORTED.includes(browser) ? browser : "en";
  } catch { return "en"; }
}

const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: (k, fb) => fb ?? k });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitialLang());

  // Reflect lang on <html> for accessibility + screen readers
  useEffect(() => {
    try { document.documentElement.lang = lang; } catch { /* noop */ }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
    // Best-effort: sync to authenticated user profile (silent if not logged in)
    api.patch("/users/me", { preferred_language: next }).catch(() => { /* not logged in or unsupported */ });
  }, []);

  const t = useCallback((key, fallback) => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
    if (dict[key] != null) return dict[key];
    if (TRANSLATIONS.en[key] != null) return TRANSLATIONS.en[key];
    return fallback ?? key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
export const useT = () => useContext(LanguageContext).t;

// Reusable toggle component — minimal segmented control matching brand
export const LanguageToggle = ({ size = "sm", className = "" }) => {
  const { lang, setLang } = useLang();
  const px = size === "sm" ? "px-2 py-1" : "px-3 py-1.5";
  return (
    <div className={`inline-flex border border-[#E5E5E5] bg-white ${className}`} data-testid="lang-toggle">
      {SUPPORTED.map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`${px} text-[10px] uppercase tracking-widest transition ${lang === l ? "bg-[#0A0A0A] text-white" : "text-[#737373] hover:bg-[#FAFAFA]"}`}
          data-testid={`lang-${l}`}
          aria-label={l === "es" ? "Español" : "English"}
          aria-pressed={lang === l}
        >
          {l}
        </button>
      ))}
    </div>
  );
};
